import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import { supabase } from './supabaseClient';
import {
  BarChart as RBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, ReferenceLine, Cell, LabelList,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '/api'

// ── THEME ─────────────────────────────────────────────────
const T = {
  bg:        '#0F1B2D',
  card:      '#1A2A3D',
  card2:     '#223347',
  border:    '#2A3F5A',
  border2:   '#3A5070',
  text:      '#F0F4F8',
  textMid:   '#FFFFFF',
  textSoft:  '#FFFFFF',
  blue:      '#3B82F6',
  blueD:     '#1D4ED8',
  blueL:     '#93C5FD',
  green:     '#10B981',
  greenL:    '#6EE7B7',
  greenD:    '#065F46',
  amber:     '#F59E0B',
  amberL:    '#FCD34D',
  amberD:    '#92400E',
  red:       '#EF4444',
  redL:      '#FCA5A5',
  redD:      '#7F1D1D',
  purple:    '#8B5CF6',
  teal:      '#14B8A6',
  grey:      '#9CA3AF',
  greyL:     '#D1D5DB',
  greyD:     '#374151',
};

const TABS = ['Summary','Industry','Profitability','Efficiency','Solvency','Growth','Peers','Valuation','Glossary'];

// ── GLOSSARY ──────────────────────────────────────────────
const GLOSSARY = {
  'ROCE':        {full:'Return on Capital Employed',plain:'How efficiently the company uses its money to generate profit. For every ₹100 invested in the business, how many rupees of profit does it earn? Higher is better.'},
  'ROE':         {full:'Return on Equity',plain:'How much profit the company makes for every rupee shareholders have invested. Higher is better.'},
  'EBIT':        {full:'Earnings Before Interest and Tax',plain:'The company\'s profit from its core business, before paying interest on loans and taxes.'},
  'EBIT Margin': {full:'EBIT as % of Revenue',plain:'What % of every rupee of sales becomes operating profit. Higher means better pricing power or cost control.'},
  'PAT':         {full:'Profit After Tax',plain:'The final profit that belongs to shareholders after all expenses, interest and taxes are paid.'},
  'PAT Margin':  {full:'Profit After Tax as % of Revenue',plain:'What % of every rupee of sales becomes final profit. Higher is better.'},
  'CFO':         {full:'Cash Flow from Operations',plain:'Actual cash generated from the business (not just accounting profit). CFO > PAT means profits are real and backed by cash.'},
  'CFO/PAT':     {full:'CFO divided by Profit After Tax',plain:'Quality check — are reported profits turning into real cash? ≥1.0x is ideal. Below 0.7x raises questions about earnings quality.'},
  'CAGR':        {full:'Compounded Annual Growth Rate',plain:'The steady annual growth rate that would take a number from its starting value to its ending value over a period. Smooths out year-to-year fluctuations.'},
  'DICC':        {full:'Debtor Days + Inventory Days − Creditor Days',plain:'How many days of cash is tied up in the operating cycle. Lower is better. Negative means the company gets paid before it pays suppliers — a strong position.'},
  'NIM':         {full:'Net Interest Margin',plain:'For banks — the difference between interest earned on loans and interest paid on deposits, as a % of assets. The core profitability measure for banks.'},
  'NPA':         {full:'Non-Performing Assets',plain:'Loans that borrowers are not repaying. Lower is better. High NPA means the bank has lent money it may not recover.'},
  'CAR':         {full:'Capital Adequacy Ratio',plain:'A measure of a bank\'s financial strength and ability to absorb losses. RBI requires minimum 12%. Higher is safer.'},
  'CIR':         {full:'Cost to Income Ratio',plain:'For banks — what % of income is spent on running costs. Lower is better. 40-50% is considered good for Indian banks.'},
  'PPOP':        {full:'Pre-Provision Operating Profit',plain:'A bank\'s operating profit before setting aside money for bad loans. Shows the underlying earning power.'},
  'AMC':         {full:'Asset Management Company',plain:'A company that manages mutual funds — collects money from investors and invests it. Earns a % fee on assets managed.'},
  'AUM':         {full:'Assets Under Management',plain:'Total value of money an AMC manages for investors. Higher AUM means more fee income.'},
  'WACC':        {full:'Weighted Average Cost of Capital',plain:'The minimum return a company must earn to satisfy all investors (both lenders and shareholders). Think of it as the hurdle rate for value creation.'},
  'DCF':         {full:'Discounted Cash Flow',plain:'A valuation method that calculates what future cash flows are worth in today\'s money. The most fundamental way to value a business.'},
  'DDM':         {full:'Dividend Discount Model',plain:'A valuation method based on the dividends a company is expected to pay in the future.'},
  'IV':          {full:'Intrinsic Value',plain:'What the business is actually worth based on fundamentals — independent of current market price.'},
  'MoS':         {full:'Margin of Safety',plain:'The difference between intrinsic value and current price, as a %. Positive means the stock is cheaper than its intrinsic value — a safety cushion.'},
  'EVA':         {full:'Economic Value Added',plain:'Whether the company is creating real wealth above and beyond its cost of capital. Positive EVA = genuine value creation. Negative EVA = value destruction.'},
  'PEG':         {full:'Price to Earnings Growth ratio',plain:'P/E ratio divided by earnings growth rate. Below 1.0 may indicate the stock is cheap relative to its growth. The growth rate must be sustainable.'},
  'EPS':         {full:'Earnings Per Share',plain:'Total profit divided by number of shares. How much profit is attributed to each share.'},
  'P/E':         {full:'Price to Earnings ratio',plain:'Current share price divided by earnings per share. How many rupees you pay for every rupee of annual profit. Compare within the same sector.'},
  'EV':          {full:'Enterprise Value',plain:'The total value of a company including its debt. Market cap + debt − cash. A more complete measure of company value than just market cap.'},
  'EBITDA':      {full:'Earnings Before Interest, Tax, Depreciation and Amortisation',plain:'A measure of operating profitability before non-cash charges. Often used in valuation multiples.'},
  'D/E':         {full:'Debt to Equity ratio',plain:'How much debt the company has relative to shareholders\' equity. Lower is generally safer. Very high D/E means heavy reliance on borrowed money.'},
  'ND/EBITDA':   {full:'Net Debt to EBITDA ratio',plain:'How many years of operating profit it would take to repay net debt. Used heavily in Real Estate and capital-intensive sectors. Below 3x is generally safe.'},
  'NSE':         {full:'National Stock Exchange',plain:'India\'s largest stock exchange. Most actively traded stocks are listed here.'},
  'BSE':         {full:'Bombay Stock Exchange',plain:'India\'s oldest stock exchange, established in 1875. Many smaller companies are listed only on BSE.'},
  'SEBI':        {full:'Securities and Exchange Board of India',plain:'India\'s stock market regulator — the equivalent of the SEC in the US.'},
  'NBFC':        {full:'Non-Banking Financial Company',plain:'A financial company that provides banking-like services (loans, credit) but does not hold a banking licence.'},
  'FMCG':        {full:'Fast Moving Consumer Goods',plain:'Everyday products sold quickly at relatively low cost — toothpaste, soap, biscuits, beverages. Companies like HUL, Nestle, Britannia.'},
  'PLI':         {full:'Production Linked Incentive',plain:'A government scheme that gives financial incentives to companies that manufacture in India and grow production.'},
  'CASA':        {full:'Current Account Savings Account ratio',plain:'For banks — proportion of deposits in current/savings accounts (cheap) vs fixed deposits (expensive). Higher CASA = lower cost of funds = better margins.'},
  'ROA':         {full:'Return on Assets',plain:'For banks — profit as a % of total assets. The standard profitability measure for banks instead of ROCE.'},
  'LTV':         {full:'Loan to Value ratio',plain:'For real estate — the loan amount as a % of property value. Lower LTV means more equity cushion and lower risk.'},
  'Material Margin':{full:'Material Margin (Revenue minus Raw Material Cost)',plain:'Revenue minus raw material cost, divided by revenue. Shows raw material intensity — not a complete gross margin since it excludes power, employee, and other manufacturing costs. Use for raw material efficiency comparison only.'},
  'Interest Cov.':{full:'Interest Coverage ratio',plain:'How many times the company\'s operating profit covers its interest payments. Higher = safer. Below 3x is a warning signal.'},
  'Debt / Equity':  {full:'Debt to Equity ratio',plain:'Total borrowings divided by shareholders\' equity. Tells you how much of the company is funded by debt vs owners\' money. Below 0.5x is conservative; above 2x is high-risk territory.'},
  'Net Debt / Cash':{full:'Net Debt or Net Cash position',plain:'Total borrowings minus cash in hand. Negative means the company has more cash than debt — a very healthy sign. Positive means net debt exposure.'},
  'Current Ratio':  {full:'Current Ratio',plain:'Current assets divided by current liabilities. Measures whether the company can pay its short-term bills using short-term resources. Above 1.5x is comfortable; below 1x is a warning sign.'},
  'Net Debt/EBITDA':{full:'Net Debt to EBITDA ratio',plain:'How many years of operating profit (before tax, interest, depreciation) it would take to repay all net debt. Below 2x is safe; above 4x is concerning for most sectors.'},
  'Asset T/O':   {full:'Asset Turnover ratio',plain:'Revenue generated per rupee of assets. Higher means the business is squeezing more sales from the same asset base.'},
  'Debtor Days': {full:'Debtor Days',plain:'The average number of days it takes to collect payment from customers. Lower is better — faster collection means cash is not stuck in receivables.'},
  'Inventory Days':{full:'Inventory Days',plain:'The average number of days the company holds stock before selling it. Lower is better for most sectors. Very high inventory days may indicate slow demand.'},
  'Emp Cost %':  {full:'Employee Cost as % of Revenue',plain:'For IT/Services companies — what % of revenue goes to employee salaries. IT companies typically run at 50-70%. Lower means better cost management.'},
  'Stock CAGR 3yr':{full:'Stock Price Compounded Annual Growth Rate — 3 Years',plain:'How much the stock price has grown annually over the past 3 years. Compare against PAT CAGR to see if market is pricing in future growth.'},
};

// ── HELPERS ───────────────────────────────────────────────
const fmt   = (n,d=1) => n!=null ? Number(n).toFixed(d) : '—';
const fmtRs = n => n!=null ? '₹'+Number(n).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g,',') : '—';
const fmtCr = n => n!=null ? '₹'+Number(n).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g,',')+' Cr' : '—';
const fmtK  = n => n!=null ? (Math.abs(n)>=1000 ? '₹'+(n/1000).toFixed(1)+'K Cr' : fmtCr(n)) : '—';
// UI-1: the ONE place a score gets rounded to a whole number for
// display — used by the headline AND the Gauge, so they can never show
// different integers for the same result.health_score. Previously the
// headline used .toFixed(0) and the Gauge used Math.round() separately
// — numerically equivalent for almost every value, but two independent
// call sites instead of one is exactly the "not a single source" shape
// this fix is about. See config/DECISIONS.md's UI-1 entry.
const displayScore = n => n!=null ? Math.round(n) : '—';

// P1-7 Part 2b: null means "not applicable" (e.g. Debt/Equity for a bank),
// never a real zero/worst-case — must render neutral, never red. JS's own
// relational operators coerce null to 0, which used to silently paint
// these red (or, for bandLow, green) depending on the thresholds; this
// explicit check removes that coercion entirely.
const band = (v,g,o) => v==null ? T.textSoft : v>=g ? T.green : v>=o ? T.amber : T.red;
const bandLow = (v,g,o) => v==null ? T.textSoft : v<=g ? T.green : v<=o ? T.amber : T.red;

const vCfg = {
  BUY:    {color:'#00E676', cardColor:'#00E676', bg:'#065F46', border:T.green,  icon:'↑', label:'BUY'},
  WATCH:  {color:'#FF8C00', cardColor:'#FF8C00', bg:'#FEF3C7', border:T.amber,  icon:'◎', label:'WATCH'},
  HOLD:   {color:'#00C2A8', cardColor:'#00C2A8', bg:'#042F2E', border:'#00C2A8',icon:'◈', label:'HOLD'},
  AVOID:  {color:'#FFFFFF', cardColor:'#FFFFFF', bg:'#7F1D1D', border:T.red,    icon:'↓', label:'AVOID'},
  REJECT: {color:'#FF2D2D', cardColor:'#FFFFFF', bg:'#3B0764', border:T.purple, icon:'✕', label:'REJECT'},
  // P1-4: neutral grey — deliberately not red/amber/green, since NOT_RATED
  // means "too much missing data to judge", not a judgement on the company.
  NOT_RATED: {color:T.greyL, cardColor:T.greyL, bg:T.greyD, border:T.grey, icon:'?', label:'NOT RATED'},
};

// ── RESPONSIVE HOOK ───────────────────────────────────────
function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h, {passive:true});
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// ── MOBILE TAB PICKER ─────────────────────────────────────
// Custom dropdown — navigates instantly on tap, no iOS "Done" button required.
function MobileTabPicker({tab, setTab}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); };
  }, [open]);
  return (
    <div ref={ref} style={{position:'relative'}}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'12px 14px',background:'#0A2A1A',border:'2px solid #39FF14',borderRadius:9,
          fontSize:15,fontWeight:700,color:'#F0F4F8',fontFamily:'inherit',cursor:'pointer',
          boxShadow:'0 0 8px #39FF1440',boxSizing:'border-box',WebkitTapHighlightColor:'transparent'}}>
        <span>{TABS[tab]}</span>
        <span style={{fontSize:22,color:'#39FF14',fontWeight:900,lineHeight:1}}>{open?'▴':'▾'}</span>
      </button>
      {open&&(
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,zIndex:300,
          background:'#0A1F12',border:'2px solid #39FF14',borderRadius:9,
          overflow:'hidden',boxShadow:'0 8px 32px rgba(0,0,0,0.7)'}}>
          {TABS.map((t,i)=>(
            <button key={t} onClick={()=>{setTab(i);setOpen(false);}}
              style={{width:'100%',textAlign:'left',padding:'14px 16px',
                background:tab===i?'#1A3A1A':'transparent',
                border:'none',borderBottom:`1px solid #1A3A1A`,
                fontSize:14,fontWeight:tab===i?700:400,
                color:tab===i?'#39FF14':'#F0F4F8',
                fontFamily:'inherit',cursor:'pointer',WebkitTapHighlightColor:'transparent'}}>
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FLAG RENDERER ─────────────────────────────────────────
function Flag({text}) {
  const isOK  = text.startsWith('[OK]');
  const isBad = text.startsWith('[!]');
  const clean = text.replace('[OK] ','').replace('[!] ','').replace('[i] ','');
  const icon  = isOK ? '✓' : isBad ? '!' : 'ℹ';
  const clr   = isOK ? T.green : isBad ? T.red : T.textSoft;
  return (
    <div style={{display:'flex',gap:10,padding:'7px 0',borderBottom:`1px solid ${T.border}`,alignItems:'flex-start'}}>
      <div style={{width:20,height:20,borderRadius:'50%',background:isOK?T.greenD:isBad?T.redD:T.card2,
        display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,
        color:clr,flexShrink:0,marginTop:1}}>{icon}</div>
      <span style={{fontSize:13,color:T.textMid,lineHeight:1.55}}>{clean}</span>
    </div>
  );
}

// ── GAUGE (SVG half-circle) ───────────────────────────────
function Gauge({score,max=100,size=130}) {
  const pct   = Math.min(1, score/max);
  const color = score>=70 ? T.green : score>=50 ? T.amber : T.red;
  const r=50, cx=65, cy=65;
  const toRad = d => d*Math.PI/180;
  const arcPt = (ang,ri) => [cx+ri*Math.cos(toRad(ang)), cy+ri*Math.sin(toRad(ang))];
  const arcPath = (a1,a2,ri) => {
    const [x1,y1]=arcPt(a1,ri); const [x2,y2]=arcPt(a2,ri);
    return `M ${x1} ${y1} A ${ri} ${ri} 0 0 1 ${x2} ${y2}`;
  };
  const ang = 180 + pct*180;
  const [nx,ny] = arcPt(ang, r-12);
  return (
    <svg width={size} height={size*0.6} viewBox="0 0 130 78" style={{overflow:'visible'}}>
      <path d={arcPath(180,360,r)} fill="none" stroke={T.border2} strokeWidth={12} strokeLinecap="round"/>
      <path d={arcPath(180, 180+pct*180, r)} fill="none" stroke={color} strokeWidth={12} strokeLinecap="round"/>
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={T.text} strokeWidth={2.5} strokeLinecap="round"/>
      <circle cx={cx} cy={cy} r={5} fill={T.text}/>
      <text x={cx} y={cy-8} textAnchor="middle" fontSize={20} fontWeight="700" fill={color}>{displayScore(score)}</text>
      <text x={cx} y={cy+4} textAnchor="middle" fontSize={10} fill={T.textSoft}>/ {max}</text>
    </svg>
  );
}

// ── COMPLETENESS BADGE ────────────────────────────────────
// P1-4: always neutral grey — this reports how much real data a layer/
// overall score was built from, not a judgement of good/bad like the
// score bar's own color, so it deliberately never borrows red/amber/green.
function CompletenessBadge({pct}) {
  if(pct==null) return null;
  return (
    <span title="Share of this layer's scoring data that was actually available (placeholders don't count)"
      style={{fontSize:11,fontWeight:600,color:T.greyL,background:T.greyD,
        border:`1px solid ${T.grey}`,borderRadius:20,padding:'1px 8px',marginLeft:8,whiteSpace:'nowrap'}}>
      {fmt(pct,0)}% data
    </span>
  );
}

// ── SCORE BAR ─────────────────────────────────────────────
function ScoreBar({label, score, max=25, completeness}) {
  const pct   = (score/max)*100;
  const color = score>=20 ? '#39FF14' : score>=15 ? '#FFD700' : score>=10 ? '#FF6B00' : '#FF2D2D';
  return (
    <div style={{marginBottom:14}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
        <span style={{fontSize:13,color:T.textMid,display:'flex',alignItems:'center'}}>{label}<CompletenessBadge pct={completeness}/></span>
        {/* UI-1: one decimal, matching health_score's own precision —
            so these four bars plus the trend-adjustment line below them
            visibly sum to the headline score. See
            config/DECISIONS.md's UI-1 entry. */}
        <span style={{fontSize:14,fontWeight:700,color:'#FFFFFF'}}>{fmt(score,1)}<span style={{fontSize:14,color:'#FFFFFF',fontWeight:400}}>/25</span></span>
      </div>
      <div style={{width:'100%',height:14,background:T.border2,borderRadius:4,overflow:'hidden'}}>
        <div style={{height:14,width:`${pct}%`,background:`linear-gradient(90deg,${color}99,${color})`,borderRadius:4,transition:'width 0.8s'}}/>
      </div>
    </div>
  );
}

// ── LAYER SCORE BANNER ────────────────────────────────────
function LayerScoreBanner({layer, label, score}) {
  const pct   = Math.min(100, (score / 25) * 100);
  const color = score >= 20 ? '#39FF14' : score >= 15 ? '#FFD700' : score >= 10 ? '#FF6B00' : '#FF2D2D';
  const interp = score >= 20 ? 'Excellent' : score >= 15 ? 'Good' : score >= 10 ? 'Fair' : 'Weak';
  return (
    <div style={{background:'#1A2A3D',border:`2px solid ${color}44`,borderRadius:12,
      padding:'16px 20px',marginBottom:16,boxShadow:`0 0 18px ${color}18`}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:color,letterSpacing:'0.1em',
            textTransform:'uppercase',marginBottom:4}}>Layer {layer} Score</div>
          <div style={{fontSize:17,fontWeight:700,color:'#F0F4F8'}}>{label}</div>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:'2.8rem',fontWeight:800,color:color,lineHeight:1}}>
            {fmt(score,1)}
            <span style={{fontSize:'1.1rem',fontWeight:400,color:'#AABBCC'}}>/25</span>
          </div>
          <div style={{fontSize:13,fontWeight:600,color:color,marginTop:2}}>{interp}</div>
        </div>
      </div>
      <div style={{width:'100%',height:10,background:'#2A3F5A',borderRadius:5,overflow:'hidden'}}>
        <div style={{height:10,width:`${pct}%`,
          background:`linear-gradient(90deg,${color}77,${color})`,
          borderRadius:5,transition:'width 0.8s'}}/>
      </div>
    </div>
  );
}

// ── METRIC CARD ───────────────────────────────────────────
function MCard({label,value,sub,color,highlight}) {
  return (
    <div style={{background:highlight?`${color}18`:T.card2,border:`1px solid ${highlight?color:T.border}`,
      borderRadius:10,padding:'12px 14px'}}>
      <div style={{fontSize:13,color:'#FFFFFF',marginBottom:5,fontWeight:500,letterSpacing:'0.03em'}}>{label}</div>
      <div style={{fontSize:22,fontWeight:700,color:color||T.text,lineHeight:1}}>{value}</div>
      {sub&&<div style={{fontSize:13,color:'#FFFFFF',marginTop:4}}>{sub}</div>}
    </div>
  );
}

// ── METRIC WITH TOOLTIP ───────────────────────────────────
function MetricWithTooltip({label,value,sub,color,highlight,comment}) {
  const [showTip,setShowTip] = useState(false);
  const info = GLOSSARY[label];
  return (
    <div style={{position:'relative'}}
      onMouseEnter={()=>setShowTip(true)}
      onMouseLeave={()=>setShowTip(false)}
      onClick={()=>info&&setShowTip(v=>!v)}>
      <div className="mob-metric-card" style={{background:highlight?`${color}18`:T.card2,border:`1px solid ${highlight?color:T.border}`,
        borderRadius:10,padding:'12px 14px',cursor:info?'help':'default'}}>
        <div style={{fontSize:13,color:'#FFFFFF',marginBottom:5,fontWeight:500,letterSpacing:'0.03em',display:'flex',alignItems:'center',gap:4}}>
          {label}
          {info&&<span style={{fontSize:10,color:T.amber,fontWeight:700,lineHeight:1}}>?</span>}
        </div>
        <div className="mob-metric-val" style={{fontSize:22,fontWeight:700,color:color||T.text,lineHeight:1}}>{value}</div>
        {sub&&<div style={{fontSize:13,color:'#FFFFFF',marginTop:4}}>{sub}</div>}
        {comment&&<div style={{fontSize:10,color:'#A0A0A0',marginTop:5,lineHeight:1.4,
          borderTop:`1px solid ${T.border}`,paddingTop:4,fontStyle:'italic',opacity:0.85}}>{comment}</div>}
      </div>
      {showTip&&info&&(
        <div className="cal-tooltip" style={{position:'absolute',bottom:'110%',left:0,zIndex:1000,
          background:T.bg,padding:'10px 14px',borderRadius:8,width:260,
          fontSize:11,lineHeight:1.6,boxShadow:'0 8px 24px rgba(0,0,0,0.6)',
          border:`1px solid ${T.border2}`,pointerEvents:'none'}}>
          <div style={{fontWeight:700,marginBottom:4,color:T.amber}}>{label} — {info.full}</div>
          <div style={{color:'#D4D4D4'}}>{info.plain}</div>
        </div>
      )}
    </div>
  );
}

// ── INLINE TOOLTIP SPAN ───────────────────────────────────
function TipSpan({children, tip}) {
  const [show, setShow] = useState(false);
  return (
    <span style={{position:'relative',display:'inline-block'}}
      onMouseEnter={()=>setShow(true)}
      onMouseLeave={()=>setShow(false)}
      onClick={()=>setShow(v=>!v)}>
      <span style={{textDecoration:'underline dotted',cursor:'help'}}>{children}</span>
      {show&&(
        <div style={{position:'absolute',bottom:'110%',left:'50%',transform:'translateX(-50%)',zIndex:1000,
          background:T.bg,padding:'10px 14px',borderRadius:8,width:280,
          fontSize:11,lineHeight:1.6,boxShadow:'0 8px 24px rgba(0,0,0,0.6)',
          border:`1px solid ${T.teal}`,color:'#FFFFFF',pointerEvents:'none',
          whiteSpace:'normal'}}>
          {tip}
        </div>
      )}
    </span>
  );
}

// ── INFO CARD (teal "from AR") ────────────────────────────
function InfoCard({label}) {
  return (
    <div style={{background:`${T.teal}10`,border:`1px solid ${T.teal}50`,
      borderRadius:10,padding:'12px 14px',display:'flex',flexDirection:'column',justifyContent:'center'}}>
      <div style={{fontSize:9,color:T.teal,marginBottom:5,fontWeight:700,letterSpacing:'0.06em'}}>FROM ANNUAL REPORT</div>
      <div style={{fontSize:13,color:T.textMid,fontWeight:500,lineHeight:1.4}}>{label}</div>
    </div>
  );
}

// ── DICC BREAKDOWN CARD ───────────────────────────────────
function DiccCard({debtor, inventory, creditor, dicc}) {
  const hasData = debtor != null && inventory != null && creditor != null;
  const net = dicc != null ? dicc : (hasData ? (debtor + inventory - creditor) : null);
  const netColor = net == null ? T.textSoft : net < 0 ? T.green : net <= 30 ? T.blue : net <= 60 ? T.amber : T.red;
  return (
    <div style={{background:T.card,border:`1px solid ${netColor}50`,borderRadius:10,padding:'8px 12px'}}>
      <div style={{fontSize:9,color:T.textMid,marginBottom:5,fontWeight:700,letterSpacing:'0.06em'}}>DICC BREAKDOWN</div>
      <div style={{fontSize:11,color:T.textMid,lineHeight:1.8}}>
        <span style={{color:T.textLight}}>Debtors</span>: <span style={{color:T.textBright,fontWeight:600}}>{debtor!=null?Math.round(debtor)+'d':'N/A'}</span><br/>
        <span style={{color:T.textLight}}>Inventory</span>: <span style={{color:T.textBright,fontWeight:600}}>{inventory!=null?Math.round(inventory)+'d':'N/A'}</span><br/>
        <span style={{color:T.textLight}}>Creditors</span>: <span style={{color:T.teal,fontWeight:600}}>{creditor!=null?Math.round(creditor)+'d':'N/A'}</span>
      </div>
      <div style={{borderTop:'1px solid #1E3A5F',marginTop:5,paddingTop:4,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <span style={{fontSize:9,color:T.textMid}}>Net (DICC)</span>
        <span style={{fontSize:14,fontWeight:700,color:netColor}}>{net!=null?Math.round(net)+'d':'N/A'}</span>
      </div>
      <div style={{fontSize:9,color:T.textMid,marginTop:2}}>
        {net==null?'Data unavailable for live fetch':net<0?'Suppliers fund operations (positive)':'Cash trapped in working capital'}
      </div>
    </div>
  );
}

// ── SECTION CARD ──────────────────────────────────────────
function SCard({title,color,children,style,className}) {
  return (
    <div className={`cal-scard${className?' '+className:''}`} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,
      padding:'1.25rem 1.5rem',marginBottom:'1.25rem',...style}}>
      {title&&<div style={{fontSize:15,fontWeight:700,letterSpacing:'0.10em',textTransform:'uppercase',
        color:color||'#FFFFFF',borderBottom:`1px solid ${T.border}`,paddingBottom:10,marginBottom:'1.1rem'}}>{title}</div>}
      {children}
    </div>
  );
}

// ── NARRATIVE BOX ─────────────────────────────────────────
function Narr({text,color}) {
  if(!text) return null;
  const _vc = {BUY:'#00E676',HOLD:'#00C2A8',WATCH:'#FF8C00',AVOID:'#FF2D2D',REJECT:'#FF2D2D',NOT_RATED:T.grey};
  const parts = text.split(/\b(BUY|HOLD|WATCH|AVOID|REJECT|NOT_RATED)\b/);
  return (
    <div style={{fontSize:15,color:T.textMid,lineHeight:1.75,background:T.card2,
      borderRadius:8,padding:'12px 16px',marginTop:12,
      borderLeft:`3px solid ${color||T.blue}`}}>
      {parts.map((p,i)=>_vc[p]?<span key={i} style={{color:_vc[p],fontWeight:700}}>{p}</span>:p)}
    </div>
  );
}

// ── COMP BAR (peer comparison) ────────────────────────────
function CompBar({value,max,color}) {
  return (
    <div style={{flex:1,background:T.border2,borderRadius:3,height:5,margin:'0 6px'}}>
      <div style={{height:5,borderRadius:3,background:color,
        width:`${Math.min(100,Math.max(0,(Math.abs(value)/max)*100))}%`}}/>
    </div>
  );
}

// ── R2b: SHARED AUTH + RATE-LIMIT HELPERS ─────────────────
// One place to build the Authorization header every authenticated
// fetch call attaches. /search, /peers, /extract-company and /report
// gained this in R2b; /analyse, /revalue, /ratio-comment and
// /read-annual-report already had it from R1-a/P0-10.
async function getAuthHeader() {
  const { data: { session } } = await supabase.auth.getSession();
  return session ? { 'Authorization': `Bearer ${session.access_token}` } : {};
}

// A 401 means the user's Supabase session has expired or was revoked —
// not a reason to silently sign them out or touch AuthWrapper. Every
// call site below just shows this and lets the user decide when to
// sign in again.
const SESSION_EXPIRED_MESSAGE = 'Your session has expired, please sign in again.';

// One shared message for every "please wait" response — a 429 rate
// limit (search, the valuation sliders, and everywhere else a backend
// rate limiter can fire) AND a 503 "reason": "server_busy" from the
// new R2c concurrency guard (/analyse, /report's slow path). Same
// helper for both so the app never has two different places that
// decide what "please wait" looks like, and never falls through to a
// raw backend string for either. retryAfterSeconds (parsed from the
// backend's Retry-After header, when present) is folded into the
// rate-limit sentence only — the concurrency guard doesn't set that
// header, since it isn't a fixed window.
function rateLimitMessage(reason, retryAfterSeconds) {
  if (reason === 'server_busy') {
    return 'Server is busy, please try again shortly.';
  }
  if (reason !== 'rate_limited') return null;
  if (retryAfterSeconds != null && retryAfterSeconds > 0) {
    return `Please slow down for a moment — try again in ${retryAfterSeconds}s.`;
  }
  return 'Please slow down for a moment.';
}

function retryAfterSecondsFrom(res) {
  // Defensive: a real fetch Response always has .headers.get, but test
  // mocks (and any other fetch-like shim) may resolve a plain object
  // without one — never let reading an optional header crash the call.
  const h = (res.headers && typeof res.headers.get === 'function') ? res.headers.get('Retry-After') : null;
  return h ? parseInt(h, 10) : null;
}

// H2: a blocked/rate-limited Screener session ("reason": "http_403" or
// "http_429") gets a different message from a timeout/connection/parse
// failure — the backend classifies which one happened; this just maps
// that machine-readable reason to the right sentence. An unrecognized or
// missing reason (including the plain network-level fetch failure in the
// catch block below, which never reaches the backend at all) falls back
// to the original "unreachable" message, unchanged from before H2.
const SEARCH_BUSY_REASONS = new Set(['http_403', 'http_429']);
function searchErrorMessage(reason, retryAfterSeconds) {
  const limited = rateLimitMessage(reason, retryAfterSeconds);
  if (limited) return limited;
  if (reason && SEARCH_BUSY_REASONS.has(reason)) {
    return 'Data source is busy, please try again in a minute.';
  }
  return 'Data source temporarily unreachable, please try again.';
}

// ── COMPANY SEARCH AUTOCOMPLETE ───────────────────────────
function CompanySearch({displayValue,onSelect,placeholder,inputStyle,style}) {
  const [value,      setValue]      = useState(displayValue||'');
  const [suggestions,setSuggestions]= useState([]);
  const [searching,  setSearching]  = useState(false);
  const [showDrop,   setShowDrop]   = useState(false);
  const [searchError,setSearchError]= useState(null);
  const abortRef    = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef     = useRef(0);

  useEffect(()=>{
    if(displayValue!==undefined) setValue(displayValue);
  },[displayValue]);

  // H1: debounce so a fast typer doesn't fire one request per keystroke —
  // only the last keystroke within ~300ms actually hits the network. The
  // AbortController still cancels any request already in flight when a
  // newer one starts, and reqIdRef guards against a late, stale response
  // (one that wasn't aborted in time) overwriting a newer one's results.
  const runSearch = async (val, reqId) => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try{
      const authHeader = await getAuthHeader();
      const res=await fetch(`${API_URL}/search?q=${encodeURIComponent(val)}`,{signal:ctrl.signal,headers:authHeader});
      const retryAfterSeconds = retryAfterSecondsFrom(res);
      const data=await res.json();
      if(reqIdRef.current!==reqId) return;  // a newer search has started — ignore this stale response
      if(res.status===401){
        setSuggestions([]);
        setSearchError(SESSION_EXPIRED_MESSAGE);
        setSearching(false);
        return;
      }
      setSuggestions(data.results||[]);
      setSearchError(data.error ? searchErrorMessage(data.reason, retryAfterSeconds) : null);
      setSearching(false);
      if((data.results||[]).length>0) setShowDrop(true);
    }catch(err){
      if(err.name==='AbortError') return;
      if(reqIdRef.current!==reqId) return;
      setSuggestions([]);
      setSearchError(searchErrorMessage(null));
      setSearching(false);
    }
  };

  const handleChange = e => {
    const val = e.target.value;
    setValue(val);
    if(abortRef.current) abortRef.current.abort();
    if(debounceRef.current) clearTimeout(debounceRef.current);
    if(val.length>=2){
      setSearching(true); setShowDrop(true); setSearchError(null);
      const reqId = ++reqIdRef.current;
      debounceRef.current = setTimeout(()=>runSearch(val, reqId), 300);
    }else{
      setSuggestions([]); setShowDrop(false); setSearchError(null); setSearching(false);
      reqIdRef.current++;  // invalidate any in-flight/pending search
    }
  };

  return (
    <div style={{position:'relative',...style}}>
      <input type="text"
        placeholder={placeholder||'Type company name...'}
        value={value}
        onChange={handleChange}
        onFocus={()=>{ if(suggestions.length>0) setShowDrop(true); }}
        onBlur={()=>setTimeout(()=>setShowDrop(false),300)}
        style={{width:'100%',padding:'10px 14px',background:T.card2,
          border:`1px solid ${T.border2}`,borderRadius:8,fontSize:14,
          color:T.text,outline:'none',boxSizing:'border-box',fontFamily:'inherit',
          ...inputStyle}}
      />
      {showDrop&&(
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:100,
          background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,
          marginTop:4,maxHeight:220,overflowY:'auto',
          boxShadow:'0 8px 24px rgba(0,0,0,0.4)'}}>
          {searching&&(
            <div style={{padding:'10px 14px',fontSize:13,color:T.textSoft}}>Searching...</div>
          )}
          {!searching&&suggestions.length===0&&value.length>=2&&searchError&&(
            <div style={{padding:'10px 14px',fontSize:13,color:T.amber}}>
              {searchError}
            </div>
          )}
          {!searching&&suggestions.length===0&&value.length>=2&&!searchError&&(
            <div style={{padding:'10px 14px',fontSize:13,color:T.textSoft,cursor:'pointer'}}
              onClick={()=>{setShowDrop(false);setValue('');onSelect('','');}}>
              No company found
            </div>
          )}
          {suggestions.map((s,i)=>(
            <div key={i}
              onClick={()=>{
                setValue(`${s.name} — ${s.ticker}`);
                setSuggestions([]);setShowDrop(false);
                onSelect(s.ticker,s.name);
              }}
              style={{padding:'10px 14px',fontSize:13,color:T.textMid,
                cursor:'pointer',borderBottom:`1px solid ${T.border}`,
                display:'flex',justifyContent:'space-between',alignItems:'center'}}
              onMouseEnter={e=>e.currentTarget.style.background=T.card2}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}
            >
              <span>{s.name}</span>
              <span style={{fontSize:11,color:T.amber,fontWeight:600,marginLeft:8}}>{s.ticker}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SIMPLE BAR CHART (pure CSS/SVG, no library) ───────────
function BarChart({data, height=60}) {
  const vals = data.map(d => d.value);
  const maxV = Math.max(...vals.map(Math.abs), 1);
  const bw = 12, gap = 5, groupGap = 18;
  const padL = 28, padB = 28, padT = 18;

  // Build x positions with group gaps
  let x = padL;
  const bars = data.map((d, i) => {
    const pos = x;
    const isLast = i === data.length - 1;
    const nextIsNewGroup = !isLast && data[i+1].group !== d.group;
    x += bw + gap + (nextIsNewGroup ? groupGap : 0);
    return {...d, x: pos};
  });

  const svgW = x + 10;
  const svgH = height + padB + padT;

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${svgW} ${svgH}`}
      preserveAspectRatio="none" style={{display:'block'}}>

      {/* Gridlines */}
      {[0.25,0.5,0.75,1].map(f => {
        const y = padT + height - f * height;
        const val = Math.round(maxV * f);
        return (
          <g key={f}>
            <line x1={padL} y1={y} x2={svgW-4} y2={y}
              stroke="#2A3F5A" strokeWidth={0.5} strokeDasharray="3 3"/>
            <text x={padL-4} y={y} textAnchor="end" dominantBaseline="central"
              fontSize={5} fill="#FFFFFF">
              {val>0?'+':''}{val}%
            </text>
          </g>
        );
      })}

      {/* Zero baseline */}
      <line x1={padL} y1={padT+height} x2={svgW-4} y2={padT+height}
        stroke="#3A5070" strokeWidth={1}/>

      {/* Group labels */}
      {['Sales','PAT','Stock'].map(grp => {
        const grpBars = bars.filter(b => b.group === grp);
        if(!grpBars.length) return null;
        const cx = grpBars[0].x + ((grpBars[grpBars.length-1].x + bw) - grpBars[0].x)/2;
        return (
          <text key={grp} x={cx} y={padT+height+22} textAnchor="middle"
            fontSize={6} fontWeight={700} fill="#FFFFFF">
            {grp} CAGR
          </text>
        );
      })}

      {/* Bars */}
      {bars.map((d, i) => {
        const isNeg = d.value < 0;
        const bh = Math.max(3, (Math.abs(d.value)/maxV)*height);
        const barY = isNeg ? padT+height : padT+height-bh;
        const clr = d.color || '#38BDF8';
        // Negative labels go just above zero line (positive space) to avoid clashing with year labels
        const labelY = isNeg ? padT+height-7 : padT+height-bh-4;

        return (
          <g key={i}>
            <rect x={d.x} y={barY} width={bw} height={bh}
              fill={clr} rx={1} opacity={1}/>

            {/* Value label */}
            <text x={d.x+bw/2} y={labelY}
              textAnchor="middle" fontSize={4.5} fontWeight={700} fill={clr}>
              {d.value>0?'+':''}{fmt(d.value)}%
            </text>

            {/* Year label below baseline */}
            <text x={d.x+bw/2} y={padT+height+12}
              textAnchor="middle" fontSize={5} fill="#FFFFFF">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
// ── DUPONT VISUAL ─────────────────────────────────────────
function DuPont({nm,at,em,roe}) {
  const items = [
    {label:'Net Margin',    value:fmt(nm)+'%',  sub:'Profit per ₹ of sales', color:T.teal},
    {label:'×',             value:null},
    {label:'Asset Turnover',value:fmt(at)+'x',  sub:'Sales per ₹ of assets',  color:T.teal},
    {label:'×',             value:null},
    {label:'Equity Mult.',  value:fmt(em)+'x',  sub:'Leverage factor',         color:em>2.5?T.amber:T.green},
    {label:'=',             value:null},
    {label:'ROE',           value:fmt(roe)+'%', sub:'Return on equity',        color:roe>15?T.green:T.amber, highlight:true},
  ];
  return (
    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:16}}>
      {items.map((item,i)=>(
        item.value===null
          ? <div key={i} style={{fontSize:22,fontWeight:700,color:T.textSoft,padding:'0 2px'}}>{item.label}</div>
          : <div key={i} style={{flex:1,minWidth:90,background:item.highlight?`${T.blue}20`:T.card2,
              border:`1px solid ${item.highlight?T.blue:T.border}`,borderRadius:10,padding:'10px 12px',textAlign:'center'}}>
              <div style={{fontSize:13,color:T.textSoft,marginBottom:4}}>{item.label}</div>
              <div style={{fontSize:22,fontWeight:700,color:item.color}}>{item.value}</div>
              <div style={{fontSize:13,color:T.textSoft,marginTop:3}}>{item.sub}</div>
            </div>
      ))}
    </div>
  );
}

// ── VALUATION BAR CHART (recharts) ───────────────────────
function ValuationChart({dcf, multiples, ddm, blended, cmp}) {
  const data = [
    {name:'DCF',      value:Math.round(dcf||0),      fill:'#00FFFF'},
    {name:'Multiples',value:Math.round(multiples||0), fill:'#39FF14'},
    {name:'DDM',      value:Math.round(ddm||0),       fill:'#FFD700'},
    {name:'Blended',  value:Math.round(blended||0),   fill:'#FF8C00'},
    {name:'CMP',      value:Math.round(cmp||0),        fill:T.textSoft},
  ].filter(d=>d.value>0);
  if(!data.length) return null;
  return (
    <div style={{height:220,marginTop:8}}>
      <ResponsiveContainer width="100%" height="100%">
        <RBarChart data={data} margin={{top:25,right:10,left:10,bottom:5}}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
          <XAxis dataKey="name" tick={{fill:T.textSoft,fontSize:11}} axisLine={false} tickLine={false}/>
          <YAxis tick={{fill:T.textSoft,fontSize:10}} axisLine={false} tickLine={false}
            tickFormatter={v=>'₹'+v.toLocaleString('en-IN')}/>
          <RTooltip formatter={(v)=>['₹'+v.toLocaleString('en-IN'),'Value']}
            contentStyle={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,fontSize:12}}
            labelStyle={{color:T.textMid}}
            cursor={false}/>
          <ReferenceLine y={cmp} stroke={T.red} strokeDasharray="4 2" label={{value:'CMP',fill:T.red,fontSize:10}}/>
          <Bar dataKey="value" radius={[4,4,0,0]} activeBar={false}>
            {data.map((d,i)=><Cell key={i} fill={d.fill}/>)}
            <LabelList dataKey="value" position="top"
              formatter={v=>'₹'+v.toLocaleString('en-IN')}
              style={{fontSize:13,fontWeight:700,fill:'#FFFFFF'}}/>
          </Bar>
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
}


// ── REVALUE ERROR MESSAGES ────────────────────────────────
// R1-b: reason-coded, same spirit as H2's searchErrorMessage — the
// backend classifies which failure happened; this only maps that
// machine-readable reason to the sentence the user sees.
const REVALUE_BUSY_REASONS = new Set(['http_403', 'http_429']);
function revalueErrorMessage(reason, retryAfterSeconds) {
  if (reason === 'no_recent_analysis') {
    return 'Your analysis has expired - please run the analysis again to use the sliders.';
  }
  const limited = rateLimitMessage(reason, retryAfterSeconds);
  if (limited) return limited;
  if (reason && REVALUE_BUSY_REASONS.has(reason)) {
    return 'Data source is busy, please try again in a minute.';
  }
  return 'Data source temporarily unreachable, please try again.';
}

// ── MAIN APP ──────────────────────────────────────────────
export default function App() {
  const isMobile = useIsMobile();
  const [ticker,      setTicker]      = useState('');
  const [companyName, setCompanyName] = useState('');
  const [file,        setFile]        = useState(null);
  const [peerLoading, setPeerLoading] = useState(false);
  const [peerRequested, setPeerRequested] = useState(false); // becomes true once the user asks for peer comparison
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError,   setPdfError]   = useState('');
  const [overrideTicker, setOverrideTicker] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [peerRes,  setPeerRes]  = useState([]);
  const [peerError, setPeerError] = useState('');
  const [error,    setError]    = useState('');
  const [progressStep, setProgressStep] = useState(0);
  const [tab,      setTab]      = useState(0);
  // R1-b: slider state. Values come straight from /analyse's own
  // dcf_wacc / dcf_g_company / dcf_terminal_growth — no local
  // re-derivation. "touched" tracks which sliders the user has actually
  // moved, so /revalue is sent only the assumptions that changed, not
  // all three on every call (see runRevalue below).
  const [sWacc,    setSWacc]    = useState(null);
  const [sTg,      setSTg]      = useState(null);
  const [sGrow,    setSGrow]    = useState(null);
  const [wTouched, setWTouched] = useState(false);
  const [gTouched, setGTouched] = useState(false);
  const [tgTouched,setTgTouched]= useState(false);
  const [revalResult, setRevalResult] = useState(null);   // latest /revalue response, or null
  const [revalLoading,setRevalLoading]= useState(false);
  const [revalError,  setRevalError]  = useState(null);    // {message, reason} or null
  const revalAbortRef    = useRef(null);
  const revalDebounceRef = useRef(null);
  const revalReqIdRef    = useRef(0);

  // Keep sliders in sync if result changes outside of analyse() (e.g. peer override)
  useEffect(() => {
    if (result && result.dcf_wacc != null) {
      setSWacc(result.dcf_wacc); setSTg(result.dcf_terminal_growth); setSGrow(result.dcf_g_company);
      setWTouched(false); setGTouched(false); setTgTouched(false);
      setRevalResult(null); setRevalError(null);
    }
  }, [result]);
  const [ratioComments, setRatioComments] = useState({});
  const [liveMode, setLiveMode] = useState(false);
  const fileRef = useRef(null);
  const isNavPop = useRef(false); // true when tab change originated from popstate

  // ── Screen protection (analysis result tabs only) ──
  // Tiled watermark + copy/contextmenu deterrents + Ctrl+C/X/A/P/S blocking + print suppression.
  // Deterrent only — OS-level screenshots and screen recording cannot be blocked from a browser.
  const [userEmail, setUserEmail] = useState('');
  const [protectionToast, setProtectionToast] = useState('');
  useEffect(() => {
    supabase.auth.getSession().then(({data:{session}}) => {
      if (session?.user?.email) setUserEmail(session.user.email);
    }).catch(()=>{});
  }, []);
  useEffect(() => {
    if (!protectionToast) return;
    const t = setTimeout(() => setProtectionToast(''), 2400);
    return () => clearTimeout(t);
  }, [protectionToast]);
  useEffect(() => {
    if (!result) return;
    const onKey = (e) => {
      const m = e.metaKey || e.ctrlKey;
      const k = (e.key||'').toLowerCase();
      if (m && (k==='c' || k==='x' || k==='a' || k==='p' || k==='s')) {
        e.preventDefault(); e.stopPropagation();
        setProtectionToast('Copying disabled — please refer to your downloaded PDF report');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [result]);
  const triggerProtectionToast = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setProtectionToast('Copying disabled — please refer to your downloaded PDF report');
  };

  // One-time mount: force-reset to landing on every fresh page load (so the user never sees
  // stale analysis results from a prior session restored via browser back/forward cache or
  // history-state persistence). Then register the popstate handler for in-session back nav.
  useEffect(() => {
    window.history.replaceState({callidus:'landing'}, '');
    setResult(null);
    setTab(0);
    const onPop = (e) => {
      const s = e.state;
      if (s?.callidus === 'result') {
        isNavPop.current = true;
        setTab(s.tab ?? 0);
      } else if (s?.callidus === 'landing') {
        isNavPop.current = true;
        setResult(null);
        setTab(0);
      }
    };
    // bfcache restore: when the page is brought back from the browser's back-forward
    // cache (event.persisted=true), the JS heap and React state are still hydrated from
    // the previous session. Force a clean slate.
    const onPageShow = (e) => {
      if (e.persisted) {
        setResult(null);
        setTab(0);
        setError('');
        setPeerRes([]);
        setRatioComments({});
        window.history.replaceState({callidus:'landing'}, '');
      }
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  // Push a history entry whenever result loads OR user switches tab.
  // Skip the push if this change was triggered by popstate (isNavPop) to avoid loops.
  useEffect(() => {
    if (!result) return;
    if (isNavPop.current) { isNavPop.current = false; return; }
    window.history.pushState({callidus:'result', tab}, '');
  }, [result, tab]);

  const PROGRESS_STAGES = [
    'Fetching financial data from Screener.in...',
    'Running sector detection and scoring...',
    'Computing valuation and intrinsic value...',
    'Generating narratives and report...',
  ];

  const analyse = async (useLive=false) => {
    if(!useLive && !file){setError('Please upload the Screener Excel file, or click "Analyse Live" to fetch directly from Screener.in.');return;}
    if(!ticker){setError('Ticker not found — please type the NSE/BSE ticker manually in the search box.');return;}
    setError('');setResult(null);setPeerRes([]);setPeerLoading(false);setPeerRequested(false);setOverrideTicker('');setLoading(true);setTab(0);setRatioComments({});setProgressStep(0);
    const iv=setInterval(()=>{setProgressStep(prev=>Math.min(3,prev+1));},8000);
    const ctrl=new AbortController();
    const tid=setTimeout(()=>ctrl.abort(),90000); // 90s hard limit — P0-9
    try {
      const authHeader = await getAuthHeader();
      const fd=new FormData();
      if(!useLive && file) fd.append('file',file);
      fd.append('ticker',ticker.toUpperCase());
      const res=await fetch(`${API_URL}/analyse`,{method:'POST',body:fd,signal:ctrl.signal,headers:authHeader});
      const retryAfterSeconds = retryAfterSecondsFrom(res);
      const data=await res.json();
      clearTimeout(tid);clearInterval(iv);setProgressStep(0);
      if(res.status===401){
        setError(SESSION_EXPIRED_MESSAGE);
        setLoading(false);
      } else if(res.status===429||res.status===503){
        // 429 = rate_limited, 503 = server_busy (R2c concurrency guard)
        // — both routed through the one shared helper, never a raw
        // backend string.
        setError(rateLimitMessage(data.reason, retryAfterSeconds) || 'Please try again shortly.');
        setLoading(false);
      } else if(data.error||data.detail){
        setError(data.error||data.detail);
        setLoading(false);
      } else {
        setResult(data);
        setLoading(false);
        setLiveMode(data.data_source==='screener_live');
        setSWacc(data.dcf_wacc); setSTg(data.dcf_terminal_growth); setSGrow(data.dcf_g_company);
        setWTouched(false); setGTouched(false); setTgTouched(false);
        setRevalResult(null); setRevalError(null);
        // Fetch AI ratio comments asynchronously (non-blocking)
        fetch(`${API_URL}/ratio-comment`,{
          method:'POST',
          headers:{'Content-Type':'application/json', ...authHeader},
          body:JSON.stringify({
            company_name:data.company_name, sector:data.sector,
            market_cap:data.market_cap, roce:data.roce, roe:data.roe,
            pat_margin:data.pat_margin, cfo_pat:data.cfo_pat,
            interest_coverage:data.interest_coverage, debt_equity:data.debt_equity,
            eva:data.eva, margin_of_safety:data.margin_of_safety,
            nim:data.nim||0, cir:data.cir||0,
          })
        }).then(r=>r.json()).then(d=>{if(d.available) setRatioComments(d.comments||{});}).catch(()=>{});
        // Peer comparison is no longer auto-fetched — see runPeerComparison(), triggered
        // on-demand from the Peers tab, to avoid a full extra pipeline re-run per peer on
        // every analysis regardless of whether the user ever opens that tab.
      }
    }catch(e){
      clearTimeout(tid);clearInterval(iv);
      if(e.name==='AbortError'){
        setError('The analysis is taking too long. Please try again in a minute.');
      } else {
        setError('Connection error — could not reach the analysis service. Please try again.');
      }
      setLoading(false);
    }
  };

  // R1-b: one engine — the backend. Moving a slider sends ONLY the
  // assumptions the user has actually touched to POST /revalue, which
  // runs the exact same compute_dcf()/blend_valuation()/finalize_verdict()
  // functions /analyse itself calls. No valuation math lives here.
  const runRevalue = async (overrides, reqId) => {
    setRevalLoading(true);
    const ctrl = new AbortController();
    revalAbortRef.current = ctrl;
    try {
      const authHeader = await getAuthHeader();
      const res = await fetch(`${API_URL}/revalue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(overrides),
        signal: ctrl.signal,
      });
      const retryAfterSeconds = retryAfterSecondsFrom(res);
      const data = await res.json();
      if (revalReqIdRef.current !== reqId) return;  // a newer slider move started — ignore this stale response
      if (res.status === 401) {
        setRevalError({ message: SESSION_EXPIRED_MESSAGE, reason: 'unauthorized' });
        setRevalResult(null);
      } else if (!res.ok) {
        setRevalError({ message: revalueErrorMessage(data.reason, retryAfterSeconds), reason: data.reason });
        setRevalResult(null);
      } else {
        setRevalResult(data);
        setRevalError(null);
      }
      setRevalLoading(false);
    } catch (e) {
      if (e.name === 'AbortError') return;
      if (revalReqIdRef.current !== reqId) return;
      setRevalError({ message: revalueErrorMessage(null), reason: null });
      setRevalResult(null);
      setRevalLoading(false);
    }
  };

  // Debounced (~300ms) — fires once per pause in dragging, not once per
  // onChange event. Re-runs whenever a touched slider's value changes;
  // sends only the assumptions actually touched, so "Reset" (which
  // clears all three touched flags) omits all three and the backend
  // falls back to the cached analysis's own defaults.
  useEffect(() => {
    if (!result || (!wTouched && !gTouched && !tgTouched)) return;
    if (revalAbortRef.current) revalAbortRef.current.abort();
    const reqId = ++revalReqIdRef.current;
    if (revalDebounceRef.current) clearTimeout(revalDebounceRef.current);
    revalDebounceRef.current = setTimeout(() => {
      const overrides = { ticker: result.ticker };
      if (wTouched)  overrides.wacc_pct            = sWacc;
      if (gTouched)  overrides.growth_yr1_pct      = sGrow;
      if (tgTouched) overrides.terminal_growth_pct = sTg;
      runRevalue(overrides, reqId);
    }, 300);
    return () => clearTimeout(revalDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sWacc, sTg, sGrow, wTouched, gTouched, tgTouched, result]);

  const resetSliders = () => {
    if (revalDebounceRef.current) clearTimeout(revalDebounceRef.current);
    if (revalAbortRef.current) revalAbortRef.current.abort();
    revalReqIdRef.current++;  // invalidate any in-flight/pending revalue
    setSWacc(result.dcf_wacc); setSTg(result.dcf_terminal_growth); setSGrow(result.dcf_g_company);
    setWTouched(false); setGTouched(false); setTgTouched(false);
    setRevalResult(null); setRevalError(null); setRevalLoading(false);
  };

  // The numbers currently on screen: the latest /revalue response once
  // any slider has been touched, otherwise the original /analyse result.
  const displayedValuation = result ? {
    dcfValue:   revalResult ? revalResult.dcf_value       : result.dcf_value,
    // P3-8a: plain-language note for when dcfValue is None because the
    // DCF itself isn't meaningful (never a raw backend reason code).
    dcfNotMeaningfulNote: revalResult ? revalResult.dcf_not_meaningful_note : result.dcf_not_meaningful_note,
    blended:    revalResult ? revalResult.blended_iv       : result.blended_iv,
    mos:        revalResult ? revalResult.margin_of_safety : result.margin_of_safety,
    verdict:    revalResult ? revalResult.verdict_at_these_assumptions : result.verdict,
    verdictReason: revalResult ? revalResult.verdict_reason : result.verdict_reason,
    verdictChanged: revalResult ? revalResult.verdict_changed_from_analysis : false,
    assumptionsUsed: revalResult ? revalResult.assumptions_used : null,
    sensitivity: revalResult ? revalResult.sensitivity : result.sensitivity,
  } : null;

  // On-demand peer comparison — no longer runs automatically after analyse(). Fetches
  // ranked peer candidates then tries them sequentially with fallthrough (one /analyse
  // call at a time, in ranked order) until 2 succeed or all 4 are exhausted.
  const runPeerComparison = async () => {
    if(!result || peerLoading) return;
    setPeerRequested(true);
    setPeerLoading(true);
    setPeerError('');
    try {
      const authHeader = await getAuthHeader();
      let pd = {peers:[]};
      try {
        const pres = await fetch(`${API_URL}/peers?ticker=${encodeURIComponent(ticker.toUpperCase())}&market_cap=${result.market_cap||0}`,{headers:authHeader});
        if(pres.status===401){
          setPeerError(SESSION_EXPIRED_MESSAGE);
          return;
        }
        if(pres.status===429){
          setPeerError(rateLimitMessage('rate_limited', retryAfterSecondsFrom(pres)));
          return;
        }
        pd = await pres.json();
      } catch {
        // network failure — fall through with the empty-peers default,
        // same silent fallback this call site always had.
      }
      const candidates=(pd.peers||[]).slice(0,4);
      const pr=[];
      for(const p of candidates){
        if(pr.length>=2) break;
        const ctrl=new AbortController();
        const tid=setTimeout(()=>ctrl.abort(),35000); // 35s per peer timeout
        const pfd=new FormData(); pfd.append('ticker',p.ticker);
        try{
          const r=await fetch(`${API_URL}/analyse`,{method:'POST',body:pfd,signal:ctrl.signal,headers:authHeader});
          const d=await r.json();
          clearTimeout(tid);
          if(!(d.error||d.detail)) pr.push(d);
        }catch{
          clearTimeout(tid);
        }
      }
      setPeerRes(pr);
    } finally {
      setPeerLoading(false);
    }
  };

  const runPeerOverride = async (t) => {
    if(!t) return;
    setPeerRequested(true);
    setPeerLoading(true);
    try{
      const authHeader = await getAuthHeader();
      const pfd=new FormData(); pfd.append('ticker',t.toUpperCase());
      const r=await fetch(`${API_URL}/analyse`,{method:'POST',body:pfd,headers:authHeader});
      const d=await r.json();
      if(!d.error&&!d.detail) setPeerRes(prev=>[d,...prev.filter(p=>p.ticker!==d.ticker)].slice(0,2));
    }catch{}
    setPeerLoading(false);
  };

  // R2b: extracted from an inline onChange handler so auth-header
  // attachment and 401/429 handling (both new this pass, since
  // /extract-company and /search are now authenticated) have somewhere
  // readable to live. companyName doubles as this flow's only feedback
  // channel, exactly as before.
  const handleExtractCompanyFile = async (f) => {
    setFile(f); setCompanyName('Searching...'); setTicker('');
    const timer=setTimeout(()=>{setCompanyName(prev=>prev==='Searching...'?'Not found — enter ticker manually':'');},10000);
    try{
      const authHeader = await getAuthHeader();
      const fd=new FormData(); fd.append('file',f);
      const res=await fetch(`${API_URL}/extract-company`,{method:'POST',body:fd,headers:authHeader});
      clearTimeout(timer);
      if(res.status===401){ setCompanyName(SESSION_EXPIRED_MESSAGE); return; }
      if(res.status===429){ setCompanyName(rateLimitMessage('rate_limited', retryAfterSecondsFrom(res))); return; }
      const data=await res.json();
      if(data.company_name){
        const sres=await fetch(`${API_URL}/search?q=${encodeURIComponent(data.company_name)}`,{headers:authHeader});
        if(sres.status===401){ setCompanyName(SESSION_EXPIRED_MESSAGE); return; }
        if(sres.status===429){ setCompanyName(rateLimitMessage('rate_limited', retryAfterSecondsFrom(sres))); return; }
        const sdata=await sres.json();
        if(sdata.results&&sdata.results.length>0){
          const top=sdata.results[0];
          setTicker(top.ticker);
          setCompanyName(top.name+' — '+top.ticker);
        }else{
          setCompanyName(data.company_name);
        }
      }
    }catch(err){
      clearTimeout(timer);
      setCompanyName('');
    }
  };

  const vc = result ? (vCfg[result.verdict]||vCfg.WATCH) : null;
  const interpColor = (i) => ({'Very Strong':'#00FFFF','Strong':'#00E676','Healthy':'#39FF14','Average':'#FFD700','Weak':'#FF6B00','Critical':'#FF2D2D'}[i]||'#FFFFFF');
  const narr = result?.narratives||{};
  const l1n = result?.layer1_narrative||{};
  const l2n = result?.layer2_narrative||{};
  const l3n = result?.layer3_narrative||{};

  // ── GLOSSARY STANDALONE (accessible without analysis) ──
  if(!result&&!loading&&tab===8) return (
    <div style={{fontFamily:"'Segoe UI',system-ui,sans-serif",background:T.bg,minHeight:'100vh'}}>
      <Header/>
      <div style={{maxWidth:860,margin:'1.5rem auto',padding:'0 1rem'}}>
        <button onClick={()=>setTab(0)}
          style={{background:'none',border:`1px solid ${T.border}`,borderRadius:6,padding:'6px 14px',
            fontSize:12,color:T.textSoft,cursor:'pointer',marginBottom:16}}>
          ← Back
        </button>
        <GlossaryTab/>
      </div>
    </div>
  );

  // ── UPLOAD SCREEN ──────────────────────────────────────
  if(!result&&!loading) return (
    <div style={{fontFamily:"'Segoe UI',system-ui,sans-serif",background:T.bg,minHeight:'100vh'}}>
      <style>{`#landing-card input[type=text]::placeholder{color:#909090}.callidus-btn-live:hover:not(:disabled){background:#14B8A6!important}.callidus-btn-run:hover:not(:disabled){background:#0F3460!important}`}</style>
      <Header/>
      {/* ── HERO ── */}
      <div style={{textAlign:'center',padding:'2rem 1.5rem 1.5rem',borderBottom:`1px solid ${T.border}`}}>
        <div style={{maxWidth:700,margin:'0 auto'}}>
          <h1 style={{fontSize:30,fontWeight:800,color:'#FFFFFF',margin:'0 0 18px 0',lineHeight:1.3,letterSpacing:'-0.3px'}}>
            Know the Business Before You Buy.
          </h1>
          <p style={{fontSize:15,color:'#D0D0D0',lineHeight:1.85,margin:0}}>
            Callidus digs deep into the fundamentals of any NSE or BSE listed company —
            Profitability, Efficiency, Solvency, and Growth — and tells you exactly what the numbers
            mean in plain language. Walk away with a clear{' '}
            <span style={{fontWeight:700,color:'#FFFFFF'}}>BUY / WATCH / AVOID</span>{' '}
            verdict, a fair value estimate, and the confidence to make your own informed decision.
          </p>
        </div>
      </div>
      <div style={{maxWidth:820,margin:'1.25rem auto 2rem',padding:'0 1rem'}}>
        <div id="landing-card" className="cal-landing-card" style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:'2rem',boxShadow:'0 8px 32px rgba(0,0,0,0.4)'}}>

          {/* ── Shared ticker search — full width ── */}
          <div style={{marginBottom:'1.5rem'}}>
            <label style={{fontSize:12,color:'#FFFFFF',display:'block',marginBottom:6,fontWeight:500}}>
              Company Name or NSE Ticker
            </label>
            <CompanySearch
              displayValue={companyName||ticker}
              placeholder="Type company name e.g. Canara Robeco, Infosys, HDFC Bank..."
              onSelect={(t,n)=>{setTicker(t);setCompanyName(t?`${n} — ${t}`:'');}}
              inputStyle={{border:'1px solid #2A4A6A'}}
            />
            {ticker&&<div style={{fontSize:11,color:T.green,marginTop:4}}>✓ Ticker selected: {ticker}</div>}
            {!ticker&&file&&(
              <div style={{marginTop:6}}>
                <div style={{fontSize:11,color:T.amber,marginBottom:4}}>Ticker not found automatically — enter manually:</div>
                <input type="text" placeholder="e.g. SHRAAITECH, COFORGE..."
                  onChange={e=>setTicker(e.target.value.toUpperCase())}
                  style={{width:'100%',padding:'6px 10px',background:T.card2,
                    border:`1px solid ${T.amber}`,borderRadius:6,fontSize:13,
                    color:T.text,outline:'none',boxSizing:'border-box'}}/>
              </div>
            )}
          </div>

          {/* ── Two-path split ── */}
          <div className="cal-landing-split" style={{display:'flex',alignItems:'stretch'}}>

            {/* Path A — Live Analysis (55%) */}
            <div className="cal-landing-path-a" style={{flex:'0 0 55%',paddingRight:'1.75rem'}}>
              <div style={{fontSize:12,fontWeight:700,color:T.teal,letterSpacing:'0.07em',marginBottom:14}}>
                ⚡ LIVE ANALYSIS
              </div>
              <button onClick={()=>analyse(true)} disabled={!ticker} className="callidus-btn-live"
                style={{background:'#0F766E',
                  color:'#FFFFFF',border:'none',
                  padding:'11px 28px',borderRadius:8,fontSize:14,fontWeight:600,
                  cursor:ticker?'pointer':'not-allowed',
                  opacity:ticker?1:0.5,
                  boxShadow:ticker?'0 4px 16px #0F766E45':'none',
                  transition:'all 0.15s'}}>
                ⚡ Analyse Live
              </button>
            </div>

            {/* Vertical divider with OR */}
            <div className="cal-landing-divider" style={{flex:'0 0 40px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',position:'relative'}}>
              <div className="cal-landing-divider-line" style={{position:'absolute',top:0,bottom:0,left:'50%',transform:'translateX(-50%)',width:1,background:T.border}}/>
              <span className="cal-or-span" style={{position:'relative',zIndex:1,background:T.card,padding:'8px 0',
                fontSize:10,fontWeight:700,color:T.textSoft,letterSpacing:'0.08em'}}>OR</span>
            </div>

            {/* Path B — Excel Analysis (45%) */}
            <div className="cal-landing-path-b" style={{flex:1,paddingLeft:'1.75rem'}}>
              <div style={{fontSize:12,fontWeight:700,color:T.blue,letterSpacing:'0.07em',marginBottom:6}}>
                📤 EXCEL ANALYSIS
              </div>
              <div style={{fontSize:12,color:'#D0D0D0',marginBottom:8,lineHeight:1.6}}>
                Upload Screener.in Excel export for detailed multi-year analysis
              </div>
              <div style={{fontSize:11,color:'#A0A0A0',marginBottom:14,padding:'5px 10px',
                background:`${T.border}50`,borderRadius:6,lineHeight:1.55,fontStyle:'italic'}}>
                Screener.in → Company page → Export to Excel (Consolidated)
              </div>
              {/* Hidden native file input, triggered by custom button */}
              <input ref={fileRef} type="file" accept=".xlsx"
                onChange={e=>{const f=e.target.files[0];if(f) handleExtractCompanyFile(f);}}
                style={{display:'none'}}/>
              <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                <button onClick={()=>fileRef.current&&fileRef.current.click()}
                  style={{background:T.card2,border:`1px solid ${T.border2}`,
                    color:T.textMid,padding:'9px 14px',borderRadius:8,fontSize:13,fontWeight:500,
                    cursor:'pointer',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                  📂 {file?`${file.name.slice(0,18)}${file.name.length>18?'…':''}`:'Choose File'}
                </button>
                <button onClick={()=>analyse(false)} disabled={!ticker||!file} className="callidus-btn-run"
                  style={{background:'#1E3A5F',
                    color:'#FFFFFF',
                    border:'1px solid #14B8A6',
                    padding:'9px 18px',borderRadius:8,fontSize:13,fontWeight:600,
                    cursor:(ticker&&file)?'pointer':'not-allowed',
                    opacity:(ticker&&file)?1:0.5,
                    transition:'all 0.15s'}}>
                  Run Analysis
                </button>
              </div>
              {file&&<div style={{fontSize:11,color:T.green,marginTop:6}}>✓ {file.name}</div>}
            </div>
          </div>

          {error&&<div style={{background:`${T.red}18`,color:T.redL,padding:'10px 14px',
            borderRadius:8,fontSize:13,marginTop:'1.25rem',border:`1px solid ${T.red}40`}}>{error}</div>}

        </div>
      </div>
    </div>
  );

  // ── LOADING ────────────────────────────────────────────
  if(loading) return (
    <div style={{fontFamily:"'Segoe UI',system-ui,sans-serif",background:T.bg,minHeight:'100vh',display:'flex',flexDirection:'column'}}>
      <Header/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
      <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'2rem'}}>
        <div style={{width:40,height:40,borderRadius:'50%',border:'3px solid #1E3A5F',borderTopColor:'#14B8A6',animation:'spin 0.9s linear infinite',marginBottom:18}}/>
        <div style={{fontSize:22,fontWeight:700,color:T.text,marginBottom:4}}>Analysing {ticker.toUpperCase()}</div>
        <div style={{fontSize:13,color:'#D0D0D0',marginBottom:36}}>Analysing — this typically takes 30–60 seconds</div>
        {/* Stepper card */}
        <div style={{width:'100%',maxWidth:400,background:'#0F172A',borderRadius:14,
          borderLeft:`3px solid ${T.teal}`,padding:'1.5rem 1.75rem',
          boxShadow:'0 8px 32px rgba(0,0,0,0.5)'}}>
          {PROGRESS_STAGES.map((label,i)=>{
            const done=i<progressStep;
            const active=i===progressStep;
            const dotBg  = done?T.teal : active?T.amber : '#1E3A5F';
            const dotClr = done?'#FFFFFF' : active?'#FFFFFF' : T.textSoft;
            const txtClr = done?'#D0D0D0' : active?'#FFFFFF' : T.textSoft;
            const lineClr= done?T.teal : '#1E3A5F';
            return (
              <div key={i} style={{display:'flex',gap:14,alignItems:'flex-start'}}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0}}>
                  <div style={{width:32,height:32,borderRadius:'50%',
                    background:dotBg,border:'none',flexShrink:0,
                    display:'flex',alignItems:'center',justifyContent:'center',
                    animation:active?'pulse 1.6s ease-in-out infinite':undefined,
                    boxShadow:active?`0 0 12px ${T.amber}60`:done?`0 0 8px ${T.teal}40`:'none'}}>
                    {done
                      ?<span style={{fontSize:13,color:dotClr,fontWeight:700}}>✓</span>
                      :<span style={{fontSize:12,fontWeight:700,color:dotClr}}>{i+1}</span>}
                  </div>
                  {i<3&&<div style={{width:2,height:28,background:lineClr,margin:'3px 0',borderRadius:1}}/>}
                </div>
                <div style={{paddingTop:5,paddingBottom:i<3?28:0,flex:1}}>
                  <div style={{fontSize:14,fontWeight:active?700:400,color:txtClr,lineHeight:1.4}}>{label}</div>
                  {active&&(
                    <div style={{fontSize:12,color:T.amber,marginTop:4,display:'flex',alignItems:'center',gap:6}}>
                      <span style={{display:'inline-block',width:9,height:9,
                        border:`1.5px solid ${T.amber}50`,borderTopColor:T.amber,
                        borderRadius:'50%',animation:'spin 0.85s linear infinite'}}/>
                      In progress
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── RESULTS ────────────────────────────────────────────
  const _xmlEsc = (s) => String(s).replace(/[<>&'"]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;',"'":'&apos;','"':'&quot;'}[c]));
  const _wmDate = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});
  const _wmText = `${_xmlEsc(userEmail||'callidus user')}  ·  ${_xmlEsc(result.ticker||'')}  ·  ${_wmDate}`;
  const _wmSvg = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='480' height='280'><text x='30' y='150' font-family='system-ui,sans-serif' font-size='14' fill='#ffffff' fill-opacity='0.08' transform='rotate(-25 240 140)'>${_wmText}</text></svg>`)}`;
  return (
    <div
      onContextMenu={triggerProtectionToast}
      onCopy={triggerProtectionToast}
      onCut={triggerProtectionToast}
      onDragStart={triggerProtectionToast}
      style={{fontFamily:"'Segoe UI',system-ui,sans-serif",background:T.bg,minHeight:'100vh',
              userSelect:'none',WebkitUserSelect:'none',MozUserSelect:'none',msUserSelect:'none',
              WebkitTouchCallout:'none',WebkitTapHighlightColor:'transparent'}}>
      <style>{`
        @media print { body { visibility: hidden !important; } body::after { content: 'Printing disabled — please refer to your downloaded PDF report'; visibility: visible; position: fixed; top: 40%; left: 0; right: 0; text-align: center; font-family: system-ui, sans-serif; font-size: 16px; color: #000; } }
        input, textarea, [contenteditable="true"] { user-select: text !important; -webkit-user-select: text !important; }
      `}</style>
      <div aria-hidden="true" style={{
        position:'fixed',top:0,left:0,right:0,bottom:0,
        backgroundImage:`url("${_wmSvg}")`,backgroundRepeat:'repeat',
        pointerEvents:'none',zIndex:50,
      }}/>
      <Header/>

      {/* Company Bar */}
      <div className="cal-company-bar" style={{background:T.card,borderBottom:`1px solid ${T.border}`,padding:'1rem 2rem'}}>
        <div style={{maxWidth:860,margin:'0 auto',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'1rem'}}>
          <div>
            <div style={{fontSize:20,fontWeight:700,color:T.text}}>{result.company_name}</div>
            <div style={{fontSize:15,fontWeight:500,color:'#FFFFFF',marginTop:3,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              NSE: {result.ticker} &nbsp;·&nbsp; {result.sector} &nbsp;·&nbsp; Mkt Cap: {fmtK(result.market_cap)}
              {liveMode&&<span style={{background:`${T.green}25`,color:T.green,padding:'1px 8px',borderRadius:10,fontSize:10,fontWeight:600,border:`1px solid ${T.green}40`}}>⚡ Live from Screener.in</span>}
              {!liveMode&&result.data_source==='excel'&&<span style={{background:`${T.border}60`,color:'#E0E0E0',padding:'1px 8px',borderRadius:10,fontSize:10,fontWeight:600,border:`1px solid ${T.border2}`}}>📄 Screener Excel</span>}
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'1.25rem',flexWrap:'wrap'}}>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:11,color:T.textSoft}}>Current Price</div>
              <div style={{fontSize:24,fontWeight:700,color:T.text}}>{fmtRs(result.cmp)}</div>
            </div>
            <div style={{background:vc.bg,border:`2px solid ${vc.border}`,borderRadius:10,padding:'8px 22px',textAlign:'center'}}>
              <div style={{fontSize:11,color:vc.color,fontWeight:600,opacity:0.85}}>VERDICT</div>
              <div style={{fontSize:22,fontWeight:800,color:vc.color}}>{vc.icon} {vc.label}</div>
            </div>

<button onClick={async()=>{
                if(pdfLoading) return;
                setPdfError('');
                setPdfLoading(true);
                try {
                  const authHeader = await getAuthHeader();
                  const fd=new FormData();
                  fd.append('ticker',ticker);
                  fd.append('result_json',JSON.stringify(result));
                  const ctrl=new AbortController();
                  const tid=setTimeout(()=>ctrl.abort(),60000); // 60s hard limit
                  const res=await fetch(`${API_URL}/report`,{method:'POST',body:fd,signal:ctrl.signal,headers:authHeader});
                  clearTimeout(tid);
                  if(res.status===401){ setPdfError(SESSION_EXPIRED_MESSAGE); return; }
                  if(res.status===429||res.status===503){
                    // 429 = rate_limited, 503 = server_busy (R2c concurrency
                    // guard on /report's slow path) — read the body for the
                    // real reason rather than assuming which one this is.
                    let reason=null;
                    try{ const e=await res.json(); reason=e.reason; }catch{}
                    setPdfError(rateLimitMessage(reason, retryAfterSecondsFrom(res)) || 'Please try again shortly.');
                    return;
                  }
                  if(!res.ok){
                    let msg='Server error';
                    try{const e=await res.json();msg=e.error||msg;}catch{}
                    setPdfError(msg);return;
                  }
                  const blob=await res.blob();
                  const url=URL.createObjectURL(blob);
                  const a=document.createElement('a');
                  a.href=url;
                  a.download=`Callidus_${ticker}.pdf`;
                  a.click();
                } catch(e){
                  setPdfError(e.name==='AbortError'?'Timed out — restart backend':'Network error');
                } finally {
                  setPdfLoading(false);
                }
              }}
              disabled={pdfLoading}
              style={{background:pdfError?'linear-gradient(135deg,#ef4444,#dc2626)':`linear-gradient(135deg,${T.amber},${T.amberD})`,
                color:'#fff',border:'none',padding:'8px 16px',borderRadius:8,fontSize:13,fontWeight:600,
                cursor:pdfLoading?'not-allowed':'pointer',opacity:pdfLoading?0.7:1,
                display:'flex',alignItems:'center',gap:6}}>
              {pdfLoading
                ? <><span style={{display:'inline-block',width:12,height:12,border:'2px solid rgba(255,255,255,0.35)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>Generating…</>
                : pdfError ? `⚠ ${pdfError}`
                : '⬇ PDF Report'}
            </button>

            <button onClick={()=>{setResult(null);setTab(0);}}
              style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 16px',fontSize:13,cursor:'pointer',color:T.textMid}}>
              New Analysis
            </button>
          </div>
        </div>
      </div>

      {/* Tab Bar — dropdown on mobile, scroll on desktop */}
      {isMobile ? (
        <div style={{background:T.card,borderBottom:`1px solid ${T.border}`,padding:'0.5rem 1rem'}}>
          <MobileTabPicker tab={tab} setTab={setTab}/>
        </div>
      ) : (
        <div style={{background:T.card,borderBottom:`1px solid ${T.border}`,overflowX:'auto'}}>
          <div style={{maxWidth:860,margin:'0 auto',display:'flex'}}>
            {TABS.map((t,i)=>(
              <button key={t} onClick={()=>setTab(i)} style={{
                padding:'11px 18px',fontSize:13,fontWeight:tab===i?700:400,
                color:tab===i?T.text:'#A0A0A0',
                borderBottom:tab===i?`2px solid ${T.amber}`:'2px solid transparent',
                background:'none',border:'none',borderRadius:0,cursor:'pointer',whiteSpace:'nowrap',
              }}>{t}</button>
            ))}
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="cal-tab-content" style={{maxWidth:860,margin:'1.5rem auto',padding:'0 1rem'}}>

        {/* ── TAB 1: SUMMARY ─────────────────────────────── */}
        {tab===0 && (
          <div>
            {/* Verdict + Quality split */}
            <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'1.25rem'}}>
              <div style={{background:T.card,border:`1px solid ${vc.border}40`,borderRadius:14,padding:'1.25rem 1.5rem',
                boxShadow:`0 0 24px ${vc.border}20`}}>
                <div style={{fontSize:11,fontWeight:700,letterSpacing:'0.08em',color:T.textSoft,marginBottom:8}}>BUSINESS QUALITY</div>
                <div className="mob-bq-flex" style={{display:'flex',alignItems:'center',gap:16,marginBottom:4}}>
                  <span className="mob-bq-score" style={{fontSize:'3rem',fontWeight:800,color:'#00FFFF',lineHeight:1}}>{displayScore(result.health_score)}/100</span>
                  <span className="mob-bq-interp" style={{fontSize:'2.2rem',fontWeight:700,color:interpColor(result.interpretation),lineHeight:1}}>{result.interpretation}</span>
                </div>
              </div>
              <div style={{background:vc.bg,border:`1px solid ${vc.border}`,borderRadius:14,padding:'1.25rem 1.5rem'}}>
                <div style={{fontSize:11,fontWeight:700,letterSpacing:'0.08em',color:vc.cardColor,opacity:0.8,marginBottom:8}}>VALUATION VERDICT</div>
                <div className="mob-verdict-label" style={{fontSize:28,fontWeight:800,color:vc.cardColor,marginBottom:4}}>{vc.icon} {vc.label}</div>
                <div style={{fontSize:13,color:vc.cardColor,opacity:0.85}}>{result.verdict_reason}</div>
              </div>
            </div>

            {/* P1-0e: limited-data notice — shown whenever a critical input (beta) fell back to a market-average default */}
            {result.data_quality?.limited_data_note && (
              <div style={{background:'#FEF3C7',border:`1px solid ${T.amber}`,borderRadius:12,
                padding:'0.85rem 1.1rem',marginBottom:'1.25rem',display:'flex',alignItems:'flex-start',gap:10}}>
                <span style={{fontSize:16,color:T.amberD,lineHeight:1.2}}>⚠</span>
                <div style={{fontSize:13,color:T.amberD,lineHeight:1.4}}>{result.data_quality.limited_data_note}</div>
              </div>
            )}

            {/* Health Score */}
            <SCard title="Business Health Score — Four Layer Framework">
              <div style={{display:'flex',gap:'2rem',flexWrap:'wrap',alignItems:'center'}}>
                <div style={{textAlign:'center'}}>
                  <Gauge score={result.health_score} size={150}/>
                  <div style={{fontSize:12,fontWeight:600,color:result.health_score>=70?T.green:T.amber,marginTop:4}}>{result.interpretation}</div>
                  <div style={{marginTop:6,display:'flex',justifyContent:'center'}}>
                    <CompletenessBadge pct={result.data_quality?.data_completeness?.overall}/>
                  </div>
                </div>
                <div style={{flex:1,minWidth:220}}>
                  <ScoreBar label="Layer 1 — Profitability"  score={result.layer1_score} completeness={result.data_quality?.data_completeness?.layer1}/>
                  <ScoreBar label="Layer 2 — Efficiency"     score={result.layer2_score} completeness={result.data_quality?.data_completeness?.layer2}/>
                  <ScoreBar label="Layer 3 — Solvency"       score={result.layer3_score} completeness={result.data_quality?.data_completeness?.layer3}/>
                  <ScoreBar label="Layer 4 — Growth Quality" score={result.layer4_score} completeness={result.data_quality?.data_completeness?.layer4}/>
                  {/* UI-1: the one piece already folded into the
                      headline score that the four bars above never
                      showed — this is exactly why they didn't visibly
                      sum to it. See config/DECISIONS.md's UI-1 entry. */}
                  {!!result.trend_modifier && (
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:12,fontStyle:'italic',color:T.textSoft,marginTop:-4,marginBottom:10}}>
                      <span>Trend adjustment ({result.trend_modifier>0?'earnings improving':'earnings declining'})</span>
                      <span style={{fontWeight:700}}>{result.trend_modifier>0?'+':''}{fmt(result.trend_modifier,1)}</span>
                    </div>
                  )}
                </div>
              </div>
              <Narr text={narr.executive_summary} color={T.blue}/>
            </SCard>

            {/* Headline Metrics — sector-specific */}
            <SCard title="Key Financial Metrics">
              {result.sector==='Banking / NBFC'?(
                <div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="NIM"        value={fmt(result.nim)+'%'}            color={band(result.nim,3,2)}           highlight={result.nim>3}      sub="Net interest margin"   comment={ratioComments.nim}/>
                    <MetricWithTooltip label="ROE"        value={fmt(result.roe)+'%'}            color={band(result.roe,15,10)}                                        sub="Return on equity"      comment={ratioComments.roe}/>
                    <MetricWithTooltip label="CIR"        value={fmt(result.cir)+'%'}            color={bandLow(result.cir,45,55)}                                     sub="Cost to income ratio"  comment={ratioComments.cir}/>
                    <MetricWithTooltip label="PAT Margin" value={fmt(result.pat_margin)+'%'}     color={band(result.pat_margin,18,12)}                                 sub="Bottom-line margin"    comment={ratioComments.pat_margin}/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="CFO/PAT"       value={fmt(result.cfo_pat)+'x'}              color={band(result.cfo_pat,1,0.7)}   highlight={result.cfo_pat>=1} sub="Cash conversion" comment={ratioComments.cfo_pat}/>
                    <MetricWithTooltip label="P/E"           value={result.pe_ratio>0?fmt(result.pe_ratio,1)+'x':'—'} color={result.pe_ratio>0&&result.pe_ratio<15?T.green:T.amber} sub="Price to earnings"/>
                    <MetricWithTooltip label="PEG"           value={result.peg_ratio>0?fmt(result.peg_ratio)+'x':'—'} color={result.peg_ratio>0&&result.peg_ratio<1?T.green:T.amber} sub={result.peg_note||'Growth-adj P/E'}/>
                    <MetricWithTooltip label="Stock CAGR 3yr" value={result.stock_cagr_3yr?fmt(result.stock_cagr_3yr)+'%':'—'} color={band(result.stock_cagr_3yr||0,12,6)} sub="Stock return 3yr"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    <InfoCard label="CAR — Capital Adequacy Ratio"/>
                    <InfoCard label="Gross NPA %"/>
                    <InfoCard label="Net NPA %"/>
                    <InfoCard label="Provision Coverage Ratio"/>
                  </div>
                </div>
              ):result.sector==='IT / Software Services'?(
                <div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="ROCE"        value={fmt(result.roce)+'%'}           color={band(result.roce,18,10)}            highlight={result.roce>18}    sub="Return on capital"/>
                    <MetricWithTooltip label="ROE"         value={fmt(result.roe)+'%'}            color={band(result.roe,20,12)}                                           sub="Return on equity"/>
                    <MetricWithTooltip label="EBIT Margin" value={fmt(result.ebit_margin)+'%'}    color={band(result.ebit_margin,18,12)}                                   sub="Operating margin"/>
                    <MetricWithTooltip label="PAT Margin"  value={fmt(result.pat_margin)+'%'}     color={band(result.pat_margin,15,8)}                                     sub="Net margin"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="Emp Cost %"   value={fmt(l2n.emp_cost_revenue_pct||0)+'%'} color={bandLow(l2n.emp_cost_revenue_pct||0,55,70)}             sub="Of revenue"/>
                    <MetricWithTooltip label="CFO/PAT"      value={fmt(result.cfo_pat)+'x'}             color={band(result.cfo_pat,1,0.7)} highlight={result.cfo_pat>=1} sub="Cash quality"/>
                    <MetricWithTooltip label="Debtor Days"  value={fmt(l2n.debtor_days||0)+'d'}         color={bandLow(l2n.debtor_days||0,60,90)}                       sub="Collection speed"/>
                    <MetricWithTooltip label="Asset T/O"    value={fmt(l2n.asset_turnover||0)+'x'}      color={band(l2n.asset_turnover||0,1,0.5)}                       sub="Revenue per ₹ asset"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    <MetricWithTooltip label="EVA"  value={fmtCr(result.eva)}                                   color={result.eva>0?T.green:T.red}     highlight={result.eva>0}      sub="Value creation"/>
                    <MetricWithTooltip label="WACC" value={fmt(result.wacc)+'%'}                                 color={T.textMid}                                                     sub="Cost of capital"/>
                    <MetricWithTooltip label="P/E"  value={result.pe_ratio>0?fmt(result.pe_ratio,1)+'x':'—'}    color={result.pe_ratio>0&&result.pe_ratio<25?T.green:T.amber}         sub="Price to earnings"/>
                    <MetricWithTooltip label="PEG"  value={result.peg_ratio>0?fmt(result.peg_ratio)+'x':'—'}    color={result.peg_ratio>0&&result.peg_ratio<1?T.green:T.amber}        sub={result.peg_note||'Growth-adj P/E'}/>
                  </div>
                </div>
              ):result.sector==='Healthcare / Pharma'?(
                <div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="ROCE"        value={fmt(result.roce)+'%'}           color={band(result.roce,18,10)}            highlight={result.roce>18}    sub="Return on capital"/>
                    <MetricWithTooltip label="ROE"         value={fmt(result.roe)+'%'}            color={band(result.roe,15,8)}                                            sub="Return on equity"/>
                    <MetricWithTooltip label="EBIT Margin" value={fmt(result.ebit_margin)+'%'}    color={band(result.ebit_margin,16,10)}                                   sub="Operating margin"/>
                    <MetricWithTooltip label="PAT Margin"  value={fmt(result.pat_margin)+'%'}     color={band(result.pat_margin,12,6)}                                     sub="Net margin"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="CFO/PAT"       value={fmt(result.cfo_pat)+'x'}              color={band(result.cfo_pat,1,0.7)} highlight={result.cfo_pat>=1} sub="Cash quality"/>
                    <MetricWithTooltip label="Debtor Days"   value={fmt(l2n.debtor_days||0)+'d'}          color={bandLow(l2n.debtor_days||0,60,90)}                      sub="Collection speed"/>
                    <MetricWithTooltip label="Inventory Days" value={fmt(l2n.inventory_days||0)+'d'}      color={bandLow(l2n.inventory_days||0,90,150)}                  sub="Stock holding"/>
                    <MetricWithTooltip label="Asset T/O"     value={fmt(l2n.asset_turnover||0)+'x'}       color={band(l2n.asset_turnover||0,1,0.5)}                      sub="Revenue per ₹ asset"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    <MetricWithTooltip label="EVA"  value={fmtCr(result.eva)}                                   color={result.eva>0?T.green:T.red}     highlight={result.eva>0}      sub="Value creation"/>
                    <MetricWithTooltip label="P/E"  value={result.pe_ratio>0?fmt(result.pe_ratio,1)+'x':'—'}    color={result.pe_ratio>0&&result.pe_ratio<25?T.green:T.amber}         sub="Price to earnings"/>
                    <MetricWithTooltip label="PEG"  value={result.peg_ratio>0?fmt(result.peg_ratio)+'x':'—'}    color={result.peg_ratio>0&&result.peg_ratio<1?T.green:T.amber}        sub={result.peg_note||'Growth-adj P/E'}/>
                    <InfoCard label="R&D Expenditure % of Revenue"/>
                  </div>
                </div>
              ):result.sector==='FMCG / Consumer'?(
                <div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="ROCE"         value={fmt(result.roce)+'%'}          color={band(result.roce,22,15)}            highlight={result.roce>22}    sub="Return on capital"/>
                    <MetricWithTooltip label="ROE"          value={fmt(result.roe)+'%'}           color={band(result.roe,20,12)}                                           sub="Return on equity"/>
                    <MetricWithTooltip label="Material Margin" value={fmt(l1n.material_margin||0)+'%'}  color={band(l1n.material_margin||0,35,25)}                              sub="Raw material efficiency"/>
                    <MetricWithTooltip label="EBIT Margin"  value={fmt(result.ebit_margin)+'%'}   color={band(result.ebit_margin,18,12)}                                   sub="Operating margin"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="PAT Margin"  value={fmt(result.pat_margin)+'%'}     color={band(result.pat_margin,12,6)}                                     sub="Net margin"/>
                    <MetricWithTooltip label="CFO/PAT"     value={fmt(result.cfo_pat)+'x'}        color={band(result.cfo_pat,1,0.7)} highlight={result.cfo_pat>=1}         sub="Cash quality"/>
                    <DiccCard debtor={result.debtor_days||null} inventory={result.inventory_days||null} creditor={result.creditor_days||null} dicc={result.dicc}/>
                    <MetricWithTooltip label="Asset T/O"   value={fmt(l2n.asset_turnover||0)+'x'} color={band(l2n.asset_turnover||0,1.5,1)}                               sub="Revenue per ₹ asset"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    <MetricWithTooltip label="EVA"  value={fmtCr(result.eva)}                                   color={result.eva>0?T.green:T.red}     highlight={result.eva>0}      sub="Value creation"/>
                    <MetricWithTooltip label="P/E"  value={result.pe_ratio>0?fmt(result.pe_ratio,1)+'x':'—'}    color={result.pe_ratio>0&&result.pe_ratio<30?T.green:T.amber}         sub="Price to earnings"/>
                    <MetricWithTooltip label="PEG"  value={result.peg_ratio>0?fmt(result.peg_ratio)+'x':'—'}    color={result.peg_ratio>0&&result.peg_ratio<1?T.green:T.amber}        sub={result.peg_note||'Growth-adj P/E'}/>
                    <MetricWithTooltip label="WACC" value={fmt(result.wacc)+'%'}                                 color={T.textMid}                                                     sub="Cost of capital"/>
                  </div>
                </div>
              ):result.sector==='Real Estate'?(
                <div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="ROCE"       value={fmt(result.roce)+'%'}          color={band(result.roce,12,6)}             highlight={result.roce>12}    sub="Return on capital"/>
                    <MetricWithTooltip label="ROE"        value={fmt(result.roe)+'%'}           color={band(result.roe,12,6)}                                            sub="Return on equity"/>
                    <MetricWithTooltip label="PAT Margin" value={fmt(result.pat_margin)+'%'}    color={band(result.pat_margin,15,8)}                                     sub="Net margin"/>
                    <MetricWithTooltip label="CFO/PAT"    value={fmt(result.cfo_pat)+'x'}       color={band(result.cfo_pat,0.8,0.5)} highlight={result.cfo_pat>=0.8}     sub="Cash quality"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="ND/EBITDA"    value={fmt(l3n.net_debt_ebitda||0)+'x'} color={bandLow(l3n.net_debt_ebitda||99,3,5)}                        sub="Leverage (key RE metric)"/>
                    <MetricWithTooltip label="Interest Cov." value={fmt(result.interest_coverage,0)+'x'} color={band(result.interest_coverage,3,1.5)}                  sub="EBITDA / Interest"/>
                    <MetricWithTooltip label="D/E"           value={fmt(result.debt_equity)+'x'}          color={bandLow(result.debt_equity,1,2)}                       sub="Debt to equity"/>
                    <MetricWithTooltip label="EVA"           value={fmtCr(result.eva)}                    color={result.eva>0?T.green:T.red} highlight={result.eva>0}    sub="Value creation"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    <InfoCard label="Pre-sales Coverage Ratio"/>
                    <InfoCard label="Collections / Bookings Ratio"/>
                    <MetricWithTooltip label="P/E"  value={result.pe_ratio>0?fmt(result.pe_ratio,1)+'x':'—'}    color={T.amber}                                               sub="Price to earnings"/>
                    <MetricWithTooltip label="PEG"  value={result.peg_ratio>0?fmt(result.peg_ratio)+'x':'—'}    color={result.peg_ratio>0&&result.peg_ratio<1?T.green:T.amber} sub={result.peg_note||'Growth-adj P/E'}/>
                  </div>
                </div>
              ):result.sector==='Financial Services'?(
                <div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    {result.fs_subsector==='NBFC'?(
                      <MetricWithTooltip label="ROA"       value={fmt(result.roa)+'%'}            color={band(result.roa,1.0,0.5)}                                         sub="Return on assets"/>
                    ):(
                      <MetricWithTooltip label="ROCE"        value={fmt(result.roce)+'%'}           color={band(result.roce,15,8)}             highlight={result.roce>15}    sub="Return on capital"/>
                    )}
                    <MetricWithTooltip label="ROE"         value={fmt(result.roe)+'%'}            color={band(result.roe,12,8)}                                            sub="Return on equity"/>
                    <MetricWithTooltip label="EBIT Margin" value={fmt(result.ebit_margin)+'%'}    color={band(result.ebit_margin,30,18)}                                   sub={result.ebit_margin==null?"Not applicable":"Operating margin"}/>
                    <MetricWithTooltip label="PAT Margin"  value={fmt(result.pat_margin)+'%'}     color={band(result.pat_margin,20,12)}                                    sub="Net margin"/>
                  </div>
                  {result.fs_subsector==='NBFC'&&(
                    <div style={{fontSize:11,color:T.textSoft,marginBottom:10,fontStyle:'italic'}}>
                      ROA replaces ROCE for NBFC — PAT / Total Assets, the standard lending-business profitability metric.
                    </div>
                  )}
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="CFO/PAT" value={fmt(result.cfo_pat)+'x'}   color={band(result.cfo_pat,1,0.7)} highlight={result.cfo_pat>=1}                  sub="Cash quality"/>
                    <MetricWithTooltip label="EVA"     value={fmtCr(result.eva)}          color={result.eva>0?T.green:T.red} highlight={result.eva>0}                      sub="Value creation"/>
                    <MetricWithTooltip label="WACC"    value={fmt(result.wacc)+'%'}       color={T.textMid}                                                                 sub="Cost of capital"/>
                    <MetricWithTooltip label="D/E"     value={fmt(result.debt_equity)+'x'} color={bandLow(result.debt_equity,0.5,1.5)}                                     sub="Leverage ratio"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    <MetricWithTooltip label="P/E"           value={result.pe_ratio>0?fmt(result.pe_ratio,1)+'x':'—'}     color={result.pe_ratio>0&&result.pe_ratio<25?T.green:T.amber} sub="Price to earnings"/>
                    <MetricWithTooltip label="PEG"           value={result.peg_ratio>0?fmt(result.peg_ratio)+'x':'—'}     color={result.peg_ratio>0&&result.peg_ratio<1?T.green:T.amber} sub={result.peg_note||'Growth-adj P/E'}/>
                    <MetricWithTooltip label="Stock CAGR 3yr" value={result.stock_cagr_3yr?fmt(result.stock_cagr_3yr)+'%':'—'} color={band(result.stock_cagr_3yr||0,12,6)}             sub="Stock return 3yr"/>
                    <MetricWithTooltip label="Interest Cov." value={fmt(result.interest_coverage,0)+'x'}                  color={band(result.interest_coverage,5,2)}                   sub="Debt servicing"/>
                  </div>
                </div>
              ):(
                /* Default — Manufacturing */
                <div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="ROCE"         value={fmt(result.roce)+'%'}          color={band(result.roce,18,10)}            highlight={result.roce>18}    sub="Return on capital"       comment={ratioComments.roce}/>
                    <MetricWithTooltip label="ROE"          value={fmt(result.roe)+'%'}           color={band(result.roe,15,8)}                                            sub="Return on equity"        comment={ratioComments.roe}/>
                    <MetricWithTooltip label="EBIT Margin"  value={fmt(result.ebit_margin)+'%'}   color={band(result.ebit_margin,14,8)}                                    sub="Operating margin"/>
                    <MetricWithTooltip label="Material Margin" value={fmt(l1n.material_margin||0)+'%'}  color={band(l1n.material_margin||0,35,25)}                              sub="Raw material efficiency"/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:10}}>
                    <MetricWithTooltip label="PAT Margin"  value={fmt(result.pat_margin)+'%'}     color={band(result.pat_margin,10,5)}                                     sub="Net margin"       comment={ratioComments.pat_margin}/>
                    <MetricWithTooltip label="CFO/PAT"     value={fmt(result.cfo_pat)+'x'}        color={band(result.cfo_pat,1,0.7)} highlight={result.cfo_pat>=1}         sub="Cash quality"     comment={ratioComments.cfo_pat}/>
                    <DiccCard debtor={result.debtor_days||null} inventory={result.inventory_days||null} creditor={result.creditor_days||null} dicc={result.dicc}/>
                    <MetricWithTooltip label="D/E"         value={fmt(result.debt_equity)+'x'}    color={bandLow(result.debt_equity,0.5,1.5)}                              sub="Debt to equity"   comment={ratioComments.debt_equity}/>
                  </div>
                  <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                    <MetricWithTooltip label="Interest Cov." value={fmt(result.interest_coverage,0)+'x'} color={band(result.interest_coverage,10,3)}                     sub="Debt servicing"   comment={ratioComments.interest_coverage}/>
                    <MetricWithTooltip label="EVA"           value={fmtCr(result.eva)}                    color={result.eva>0?T.green:T.red} highlight={result.eva>0}      sub="Value creation"   comment={ratioComments.eva}/>
                    <MetricWithTooltip label="P/E"           value={result.pe_ratio>0?fmt(result.pe_ratio,1)+'x':'—'} color={result.pe_ratio>0&&result.pe_ratio<20?T.green:T.amber} sub="Price to earnings"/>
                    <MetricWithTooltip label="PEG"           value={result.peg_ratio>0?fmt(result.peg_ratio)+'x':'—'} color={result.peg_ratio>0&&result.peg_ratio<1?T.green:T.amber} sub={result.peg_note||'Growth-adj P/E'}/>
                  </div>
                </div>
              )}
            </SCard>

            {/* CAGR Table */}
            <SCard title="Growth Track Record — CAGR Summary">
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${T.border}`}}>
                    {['Metric','3-Year','5-Year','10-Year'].map(h=>(
                      <th key={h} style={{padding:'8px 10px',textAlign:h==='Metric'?'left':'right',color:T.textSoft,fontWeight:500}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Sales CAGR', result.sales_cagr_3yr, result.sales_cagr_5yr, result.sales_cagr_10yr],
                    ['PAT CAGR',   result.pat_cagr_3yr,   result.pat_cagr_5yr,   null],
                    ['Stock CAGR', result.stock_cagr_3yr,  result.stock_cagr_5yr,  result.stock_cagr_10yr],
                  ].map(([label,v3,v5,v10])=>(
                    <tr key={label} style={{borderBottom:`1px solid ${T.border}`}}>
                      <td style={{padding:'10px',fontWeight:600,color:T.text}}>{label}</td>
                      {[v3,v5,v10].map((v,i)=>(
                        <td key={i} style={{padding:'10px',textAlign:'right',fontWeight:600,
                          color:v==null?T.textSoft:v>=12?T.green:v>=6?T.amber:T.red}}>
                          {v!=null?(v>=0?'+':'')+fmt(v)+'%':'—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{fontSize:10,color:T.textSoft,marginTop:8,fontStyle:'italic'}}>* All CAGRs computed on TTM basis. '—' indicates insufficient history.</div>
            </SCard>

            {/* Strengths + Concerns */}
            <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'1.25rem'}}>
              <div style={{background:T.card,border:`1px solid ${T.green}30`,borderRadius:14,padding:'1.25rem 1.5rem'}}>
                <div style={{fontSize:11,fontWeight:700,letterSpacing:'0.08em',color:T.green,marginBottom:'1rem',paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>STRENGTHS</div>
                {result.all_flags?.filter(f=>f?.startsWith('[OK]')).map((f,i)=><Flag key={i} text={f}/>)}
              </div>
              <div style={{background:T.card,border:`1px solid ${T.red}30`,borderRadius:14,padding:'1.25rem 1.5rem'}}>
                <div style={{fontSize:11,fontWeight:700,letterSpacing:'0.08em',color:T.red,marginBottom:'1rem',paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>CONCERNS</div>
                {result.all_flags?.filter(f=>f?.startsWith('[!]')).map((f,i)=><Flag key={i} text={f}/>)}
              </div>
            </div>

            {result.all_flags?.some(f=>f?.startsWith('[i]'))&&(
              <SCard title="From Annual Report" color={T.teal}>
                {result.all_flags.filter(f=>f?.startsWith('[i]')).map((f,i)=><Flag key={i} text={f}/>)}
              </SCard>
            )}
          </div>
        )}

        {/* ── TAB 2: INDUSTRY ────────────────────────────── */}
        {tab===1&&(
          <div>
            <SCard title={`Sector Overview — ${result.sector}`}>
              <Narr text={narr.industry_context} color={T.teal}/>
            </SCard>
            <SCard title="Company vs Sector Benchmarks">
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:10,marginBottom:12}}>
                {[
                  result.fs_subsector==='NBFC'
                    ?['ROA',result.roa,(narr.industry_benchmarks||{}).roce_benchmark||15,'%',true]
                    :['ROCE',result.roce,(narr.industry_benchmarks||{}).roce_benchmark||15,'%',true],
                  ['EBIT Margin',result.ebit_margin,(narr.industry_benchmarks||{}).ebit_benchmark||12,'%',true],
                  ['Debt/Equity',result.debt_equity,(narr.industry_benchmarks||{}).de_benchmark||0.5,'x',false],
                  ['DICC',result.dicc,(narr.industry_benchmarks||{}).dicc_benchmark||45,'d',false],
                ].map(([label,val,bench,unit,higher])=>{
                  // P1-7 Part 2b: null means "not applicable" — never show a
                  // benchmark tick (✓/△) next to a "—" value.
                  const isNA=val==null;
                  const beats=!isNA&&(higher?val>=bench:val<=bench);
                  const clr=isNA?T.textSoft:(beats?T.green:T.amber);
                  return (
                    <div key={label} style={{background:isNA?`${T.textSoft}18`:beats?`${T.green}18`:`${T.amber}18`,borderRadius:10,
                      padding:'12px 14px',border:`1px solid ${clr}40`}}>
                      <div style={{fontSize:11,color:T.textSoft,marginBottom:4}}>{label}</div>
                      <div style={{fontSize:20,fontWeight:700,color:clr}}>{fmt(val)}{isNA?'':unit}</div>
                      <div style={{fontSize:11,color:T.textSoft,marginTop:3}}>{isNA?'Not applicable for this business':`Benchmark: ${bench}${unit}`}</div>
                      {!isNA&&(
                        <div style={{fontSize:11,fontWeight:600,color:clr,marginTop:2}}>
                          {beats?'✓ Above benchmark':'△ Below benchmark'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <Narr text={narr.company_vs_benchmarks} color={T.blue}/>
            </SCard>
            <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem'}}>
              <SCard title="Sector Tailwinds" color={T.green}>
                {(narr.industry_tailwinds||[]).map((t,i)=>(
                  <div key={i} style={{display:'flex',gap:8,padding:'7px 0',borderBottom:`1px solid ${T.border}`,fontSize:13,color:T.textMid}}>
                    <span style={{color:T.green,fontWeight:700,flexShrink:0}}>↑</span>{t}
                  </div>
                ))}
              </SCard>
              <SCard title="Sector Headwinds" color={T.red}>
                {(narr.industry_headwinds||[]).map((h,i)=>(
                  <div key={i} style={{display:'flex',gap:8,padding:'7px 0',borderBottom:`1px solid ${T.border}`,fontSize:13,color:T.textMid}}>
                    <span style={{color:T.red,fontWeight:700,flexShrink:0}}>↓</span>{h}
                  </div>
                ))}
              </SCard>
            </div>
          </div>
        )}

        {/* ── TAB 3: PROFITABILITY ───────────────────────── */}
        {tab===2&&(
          <div>
            <LayerScoreBanner layer={1} label="Profitability" score={result.layer1_score}/>
            <SCard title="DuPont Analysis — What Drives ROE?">
              <DuPont nm={l1n?.dupont?.net_margin||result.pat_margin}
                at={l1n?.dupont?.asset_turnover||0}
                em={l1n?.dupont?.equity_multiplier||0}
                roe={result.roe}/>
              <Narr text={narr.dupont} color={T.purple}/>
            </SCard>

            <SCard title="Margin Cascade — Manufacturing P&L">
              <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
                {[
                  ['Material Margin',l1n?.material_margin||0,35,25,'Raw material cost efficiency'],
                  ['EBIT Margin', result.ebit_margin,14,8, 'Operating efficiency'],
                  ['PAT Margin',  result.pat_margin, 10,5, 'Bottom line profitability'],
                ].map(([label,val,g,o,sub])=>(
                  <div key={label} style={{background:T.card2,borderRadius:10,padding:'14px',border:`1px solid ${T.border}`}}>
                    <div style={{fontSize:13,color:T.textSoft,marginBottom:6,fontWeight:600}}>{label}</div>
                    <div style={{fontSize:26,fontWeight:700,color:band(val,g,o)}}>{fmt(val)}%</div>
                    <div style={{fontSize:13,color:T.textSoft,marginTop:3}}>{sub}</div>
                    <div style={{height:5,background:T.border2,borderRadius:3,marginTop:8}}>
                      <div style={{height:5,borderRadius:3,background:band(val,g,o),width:`${Math.min(100,val*2.5)}%`}}/>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{marginTop:16}}>
                <div style={{fontSize:12,color:T.textSoft,marginBottom:10}}>Sales &amp; PAT Growth — YOY · 3yr CAGR · 5yr CAGR · 10yr CAGR</div>
                {(()=>{
                  const cData = [
                    ...(result.sales_yoy!=null||result.pat_yoy!=null
                      ? [{period:'YOY', sales:result.sales_yoy, pat:result.pat_yoy, pat_na:false}] : []),
                    ...(result.sales_cagr_3yr!=null
                      ? [{period:'3yr CAGR', sales:result.sales_cagr_3yr, pat:result.pat_cagr_3yr, pat_na:false}] : []),
                    ...(result.sales_cagr_5yr!=null
                      ? [{period:'5yr CAGR', sales:result.sales_cagr_5yr, pat:result.pat_cagr_5yr, pat_na:false}] : []),
                    ...(result.sales_cagr_10yr!=null
                      ? [{period:'10yr CAGR', sales:result.sales_cagr_10yr,
                          pat: result.pat_cagr_10yr ?? 0.001,
                          pat_na: result.pat_cagr_10yr == null}] : []),
                  ];
                  if(!cData.length) return null;
                  const SALES_CLR = '#FFD700';
                  const PAT_POS   = '#39FF14';
                  const PAT_NEG   = '#FF2D2D';
                  const PAT_NA    = '#444444';
                  const fmtV = v => v==null?'':(v>0?'+':'')+Number(v).toFixed(1)+'%';
                  const GrowthLabel = color => (props) => {
                    const {x, y, width, height, value} = props;
                    if(value==null || value==='' || isNaN(value)) return null;
                    const isNeg = Number(value) < 0;
                    const lx = x + width / 2;
                    const ly = isNeg ? y + Math.abs(height) + 16 : y - 7;
                    return (
                      <text x={lx} y={ly} textAnchor="middle"
                        fontSize={11} fontWeight={700} fill={color}
                        style={{pointerEvents:'none'}}>
                        {fmtV(value)}
                      </text>
                    );
                  };
                  const PatLabel = (props) => {
                    const {x, y, width, height, value, index} = props;
                    const entry = cData[index];
                    if(entry && entry.pat_na) {
                      return (
                        <text x={x+width/2} y={y-8} textAnchor="middle"
                          fontSize={10} fill="#888888" style={{pointerEvents:'none'}}>
                          N/A
                        </text>
                      );
                    }
                    if(value==null || isNaN(value) || Math.abs(value) < 0.01) return null;
                    const isNeg = Number(value) < 0;
                    const lx = x + width / 2;
                    const ly = isNeg ? y + Math.abs(height) + 16 : y - 7;
                    return (
                      <text x={lx} y={ly} textAnchor="middle"
                        fontSize={11} fontWeight={700} fill="#FFFFFF"
                        style={{pointerEvents:'none'}}>
                        {fmtV(value)}
                      </text>
                    );
                  };
                  return (
                    <>
                      <div style={{display:'flex',gap:20,marginBottom:8}}>
                        <span style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:'#FFFFFF'}}>
                          <span style={{width:10,height:10,background:SALES_CLR,borderRadius:2,display:'inline-block'}}/>Sales
                        </span>
                        <span style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:'#FFFFFF'}}>
                          <span style={{width:10,height:10,background:PAT_POS,borderRadius:2,display:'inline-block'}}/>PAT (positive)
                        </span>
                        <span style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:'#FFFFFF'}}>
                          <span style={{width:10,height:10,background:PAT_NEG,borderRadius:2,display:'inline-block'}}/>PAT (negative)
                        </span>
                      </div>
                      <div style={{fontSize:10,color:T.textSoft,fontStyle:'italic',marginBottom:6}}>
                        * Based on TTM (Trailing Twelve Months) — most recent 12-month rolling figures. TTM is valid at any time of year as it always captures the latest data, though minor mismatches vs. Screener annual CAGR are normal for seasonal businesses.
                      </div>
                      <div style={{height:260}}>
                        <ResponsiveContainer width="100%" height="100%">
                          <RBarChart data={cData} margin={{top:32,right:20,left:8,bottom:36}}
                            barCategoryGap="30%" barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5A" vertical={false}/>
                            <XAxis dataKey="period"
                              tick={{fill:'#FFFFFF',fontSize:12,fontWeight:600}}
                              axisLine={false} tickLine={false}/>
                            <YAxis tick={{fill:'#FFFFFF',fontSize:10}} axisLine={false} tickLine={false}
                              tickFormatter={v=>(v>0?'+':'')+v+'%'} width={44}/>
                            <ReferenceLine y={0} stroke="#3A5070" strokeWidth={1.5}/>
                            <RTooltip
                              formatter={(v,n)=>{
                                if(n==='pat'&&Math.abs(v)<0.01) return['N/A (prior loss year as base)','PAT Growth'];
                                return[fmtV(v), n==='sales'?'Sales Growth':'PAT Growth'];
                              }}
                              contentStyle={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,fontSize:12}}
                              labelStyle={{color:'#FFFFFF',fontWeight:600}}
                              cursor={{fill:'rgba(255,255,255,0.04)'}}/>
                            <Bar dataKey="sales" name="sales" fill={SALES_CLR} radius={[4,4,0,0]}>
                              <LabelList dataKey="sales" content={GrowthLabel(SALES_CLR)}/>
                            </Bar>
                            <Bar dataKey="pat" name="pat" radius={[4,4,0,0]}>
                              {cData.map((e,i)=>(
                                <Cell key={i} fill={e.pat_na ? PAT_NA : ((e.pat??0)>=0?PAT_POS:PAT_NEG)}/>
                              ))}
                              <LabelList dataKey="pat" content={PatLabel}/>
                            </Bar>
                          </RBarChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  );
                })()}
              </div>
              <Narr text={narr.profitability} color={T.green}/>
            </SCard>

            {(result.sector==='Banking / NBFC'||result.sector==='Financial Services')?(
              <SCard title="ROE vs Cost of Equity — The Value Creation Test">
                <div style={{fontSize:11,color:T.textSoft,marginBottom:10,fontStyle:'italic'}}>
                  ROCE and a capital-employed benchmark don't apply here — deposits and borrowings ARE the business, not leverage against it. ROE vs cost of equity is the correct test.
                </div>
                <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
                  <MCard label="ROA" value={fmt(result.roa)+'%'} color={T.green} sub="Return on assets"/>
                  <MCard label="ROE" value={fmt(result.roe)+'%'} color={T.green} sub="Return on equity"/>
                  <MCard label="ROE − Cost of Equity Spread" value={(result.roe-result.wacc>=0?'+':'')+fmt(result.roe-result.wacc)+'%'}
                    color={result.roe>result.wacc?T.green:T.red} highlight
                    sub={result.roe>result.wacc?'Value creating above hurdle':'Returns below cost of equity'}/>
                </div>
                <div style={{background:result.eva>0?`${T.green}15`:`${T.red}15`,borderRadius:10,
                  padding:'12px 16px',border:`1px solid ${result.eva>0?T.green:T.red}40`}}>
                  <div style={{fontSize:14,fontWeight:700,color:result.eva>0?T.green:T.red,marginBottom:4}}>
                    {result.eva>0?'✓ Economic Value Added — Value Being Created':'⚠ Negative EVA — Returns Below Cost of Equity'}
                  </div>
                  <div style={{fontSize:15,color:'#FFFFFF'}}>
                    EVA of {fmtCr(result.eva)} — {result.eva>0
                      ?'ROE exceeds the cost of equity. Every rupee of book equity is generating returns above what shareholders require — a hallmark of a quality business.'
                      :'ROE is below the cost of equity — the business is not yet earning shareholders’ required return. This does not mean the company is failing — it may be a transitional phase.'}
                  </div>
                </div>
              </SCard>
            ):(
              <SCard title="ROCE vs WACC — The Value Creation Test">
                <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
                  <MCard label="ROCE" value={fmt(result.roce)+'%'} color={T.green} sub="Return on capital employed"/>
                  <MCard label="WACC" value={fmt(result.wacc)+'%'} color={T.textMid} sub="Minimum required return"/>
                  <MCard label="ROCE − WACC Spread" value={(result.roce-result.wacc>=0?'+':'')+fmt(result.roce-result.wacc)+'%'}
                    color={result.roce>result.wacc?T.green:T.red} highlight
                    sub={result.roce>result.wacc?'Value creating above hurdle':'Returns below cost of capital'}/>
                </div>
                <div style={{background:result.eva>0?`${T.green}15`:`${T.red}15`,borderRadius:10,
                  padding:'12px 16px',border:`1px solid ${result.eva>0?T.green:T.red}40`}}>
                  <div style={{fontSize:14,fontWeight:700,color:result.eva>0?T.green:T.red,marginBottom:4}}>
                    {result.eva>0?'✓ Economic Value Added — Value Being Created':'⚠ Negative EVA — Returns Below Cost of Capital'}
                  </div>
                  <div style={{fontSize:15,color:'#FFFFFF'}}>
                    EVA of {fmtCr(result.eva)} — {result.eva>0
                      ?'every rupee of capital employed is generating returns above the hurdle rate. This is a hallmark of a quality business.'
                      :'the business is not yet earning its cost of capital. This does not mean the company is failing — it may be a transitional phase from investment or acquisition.'}
                  </div>
                </div>
              </SCard>
            )}

            <SCard title="Cash Quality — Profit vs Cash Reality">
              <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                <MCard label="CFO / PAT Ratio" value={result.cfo_pat==null?'n/a':fmt(result.cfo_pat)+'x'}
                  color={band(result.cfo_pat,1,0.7)} highlight={result.cfo_pat>=1}
                  sub={result.cfo_pat==null?'Not applicable for this business':result.cfo_pat>=1?'Profits fully backed by cash':'Monitor cash conversion'}/>
                <MCard label="Net Debt / Cash" value={result.net_debt<0?fmtCr(Math.abs(result.net_debt))+' cash':fmtCr(result.net_debt)+' debt'}
                  color={result.net_debt<0?T.green:T.amber} sub="Balance sheet strength"/>
                {(result.sector==='Banking / NBFC'||result.sector==='Financial Services')?(
                <MCard label="ROE − Ke" value={(result.roe-result.wacc>=0?'+':'')+fmt(result.roe-result.wacc)+'%'}
                  color={result.roe>result.wacc?T.green:T.red}
                  sub={result.roe>result.wacc?'Value creating':'Below cost of equity'}/>
                ):(
                <MCard label="ROCE − WACC" value={(result.roce-result.wacc>=0?'+':'')+fmt(result.roce-result.wacc)+'%'}
                  color={result.roce>result.wacc?T.green:T.red}
                  sub={result.roce>result.wacc?'Value creating':'Below cost of capital'}/>
                )}
              </div>
              {result.cfo_pat>=1&&(
                <div style={{background:`${T.green}15`,borderRadius:8,padding:'10px 14px',marginTop:12,border:`1px solid ${T.green}30`,fontSize:15,color:'#FFFFFF'}}>
                  <span style={{color:T.green,fontWeight:700}}>✓ </span>
                  CFO/PAT of {fmt(result.cfo_pat)}x confirms that reported profits are converting to real operating cash flows.
                  This is the single most important quality check — a ratio below 0.7 would raise serious questions about earnings quality.
                </div>
              )}
            </SCard>
          </div>
        )}

        {/* ── TAB 4: EFFICIENCY ──────────────────────────── */}
        {tab===3&&(
          <div>
            <LayerScoreBanner layer={2} label="Efficiency" score={result.layer2_score}/>
            <SCard title="DICC — Working Capital Cycle Explained">
              <div style={{background:T.card2,borderRadius:10,padding:'1rem',marginBottom:16,border:`1px solid ${T.border}`}}>
                <div style={{fontSize:12,color:T.textSoft,marginBottom:10}}>
                  DICC (Debtor Days + Inventory Days − Creditor Days) measures how many days of cash is locked in the operating cycle.
                  A lower number means the business collects cash faster than it needs to pay suppliers — the ideal position.
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:16}}>
                {(()=>{
                  const dd=result.debtor_days||null;
                  const id_=result.inventory_days||null;
                  const cd=result.creditor_days||null;
                  return [
                    {label:'Debtor Days',    value:dd!=null?fmt(dd)+'d':'N/A',    color:dd!=null?bandLow(dd,30,60):T.textSoft, sub:'Collection speed'},
                    {label:'+',             value:null},
                    {label:'Inventory Days', value:id_!=null?fmt(id_)+'d':'N/A', color:id_!=null?bandLow(id_,40,70):T.textSoft, sub:'Stock holding'},
                    {label:'−',             value:null},
                    {label:'Creditor Days',  value:cd!=null?fmt(cd)+'d':'N/A',   color:T.teal, sub:'Payment terms'},
                    {label:'=',             value:null},
                    {label:'Net (DICC)',     value:fmt(result.dicc)+'d',
                      color:result.dicc<0?T.green:bandLow(result.dicc,30,60), sub:result.dicc<0?'Negative = excellent':'Net cycle', highlight:true},
                  ];
                })().map((item,i)=>(
                  item.value===null
                    ? <div key={i} style={{fontSize:22,fontWeight:700,color:T.textSoft}}>{item.label}</div>
                    : <div key={i} style={{flex:1,minWidth:85,background:item.highlight?`${T.amber}20`:T.card2,
                        border:`1px solid ${item.highlight?T.amber:T.border}`,borderRadius:10,padding:'10px 12px',textAlign:'center'}}>
                        <div style={{fontSize:11,color:T.textSoft,marginBottom:4}}>{item.label}</div>
                        <div style={{fontSize:18,fontWeight:700,color:item.color}}>{item.value}</div>
                        <div style={{fontSize:11,color:T.textSoft,marginTop:3}}>{item.sub}</div>
                      </div>
                ))}
              </div>
              <Narr text={narr.efficiency} color={T.teal}/>
            </SCard>

            {l2n?.inventory_scenario&&(
              <SCard title="P&L Inventory Scenario Analysis">
                {(()=>{
                  const sc=l2n.inventory_scenario;
                  const cfg={
                    'A':{color:T.red,   label:'Double Cost Pressure',       desc:'Raw materials rising AND inventory building simultaneously — cost is being squeezed from both directions. Watch gross margins closely for compression.'},
                    'B':{color:T.green, label:'Demand-Pull Growth',           desc:'Rising raw material costs are being absorbed by growing production — demand is pulling through the supply chain. A healthy signal if gross margins hold.'},
                    'C':{color:T.amber, label:'Inventory Build-Up',           desc:'Stock is accumulating faster than sales are growing. Could be strategic seasonal stocking, but also an early demand-slowdown signal — monitor inventory days trend.'},
                    'D':{color:T.textMid,label:'Steady-State Operations',     desc:'Raw material costs and inventory are broadly stable relative to sales — no strong directional pressure in either direction. Operations are running at an even keel.'},
                    'E':{color:T.green, label:'Peak Operating Efficiency',    desc:'Selling more while using less raw material with stable inventory — operating leverage is fully at work. This is the most positive signal for margin expansion.'},
                  }[sc]||{color:T.textMid,label:'Scenario — Neutral',desc:'No clear directional signal from P&L inventory analysis.'};
                  return (
                    <div style={{background:`${cfg.color}15`,borderRadius:10,padding:'14px 16px',border:`1px solid ${cfg.color}40`}}>
                      <div style={{fontSize:14,fontWeight:700,color:cfg.color,marginBottom:6}}>{cfg.label}</div>
                      <div style={{fontSize:13,color:T.textMid}}>{cfg.desc}</div>
                    </div>
                  );
                })()}
              </SCard>
            )}

            <SCard title="Asset Efficiency">
              <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                <MCard label="Asset Turnover" value={fmt(l2n?.asset_turnover||1)+'x'}
                  color={band(l2n?.asset_turnover||1,1.5,1.0)} sub="Revenue per ₹ of assets"/>
                <MCard label="Capex / Dep" value={fmt(l2n?.capex_dep_ratio||1)+'x'}
                  color={l2n?.capex_dep_ratio>=1.5?T.green:l2n?.capex_dep_ratio>=1?T.amber:T.red}
                  sub="Growth investment vs maintenance"/>
                <MCard label="CFO / PAT" value={fmt(result.cfo_pat)+'x'}
                  color={band(result.cfo_pat,1,0.7)} sub="Cash conversion efficiency"/>
              </div>
              {l2n?.capex_dep_ratio>=1.5&&(
                <div style={{marginTop:12,background:`${T.green}15`,borderRadius:8,padding:'10px 14px',border:`1px solid ${T.green}30`,fontSize:13,color:T.textMid}}>
                  <span style={{color:T.green,fontWeight:700}}>✓ </span>
                  Capex/Depreciation of {fmt(l2n.capex_dep_ratio)}x confirms the company is investing significantly above
                  its maintenance requirement — actively building future capacity. This supports the growth thesis if returns
                  on new capital exceed WACC.
                </div>
              )}
            </SCard>
          </div>
        )}

        {/* ── TAB 5: SOLVENCY ────────────────────────────── */}
        {tab===4&&(
          <div>
            <LayerScoreBanner layer={3} label="Solvency" score={result.layer3_score}/>
            <SCard title="Debt Structure & Balance Sheet Strength">
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:16}}>
                <MetricWithTooltip label="Debt / Equity"
                  value={fmt(result.debt_equity)+'x'}
                  color={bandLow(result.debt_equity,0.5,1.5)}
                  sub="Leverage ratio"/>
                <MetricWithTooltip label="Net Debt / Cash"
                  value={result.net_debt<0?fmtCr(Math.abs(result.net_debt))+' cash':fmtCr(result.net_debt)+' debt'}
                  color={result.net_debt<0?T.green:result.net_debt<500?T.amber:T.red}
                  highlight={result.net_debt<0}
                  sub={result.net_debt<0?'Net cash position':'Net debt position'}/>
                <MetricWithTooltip label="Interest Cov."
                  value={result.interest_coverage!=null?fmt(result.interest_coverage,0)+'x':'N/A'}
                  color={result.interest_coverage!=null?band(result.interest_coverage,10,3):T.textSoft}
                  sub="EBITDA / Interest expense"/>
                <MetricWithTooltip label="Current Ratio"
                  value={result.current_ratio!=null?fmt(result.current_ratio,1)+'x':'N/A'}
                  color={result.current_ratio!=null?band(result.current_ratio,2,1):T.textSoft}
                  sub="Current assets / liabilities"/>
                <MetricWithTooltip label="Net Debt/EBITDA"
                  value={result.net_debt_ebitda!=null?fmt(result.net_debt_ebitda,1)+'x':'N/A'}
                  color={result.net_debt_ebitda!=null?bandLow(result.net_debt_ebitda,2,4):T.textSoft}
                  sub={result.net_debt_label||'Leverage vs earnings'}/>
              </div>

              {/* Current Assets vs Current Liabilities bar chart */}
              {(result.current_assets_cr!=null||result.current_liabilities_cr!=null)&&(()=>{
                const ca = result.current_assets_cr || 0;
                const cl = result.current_liabilities_cr || 0;
                const mx = Math.max(ca, cl, 1);
                const caW = Math.round(ca / mx * 100);
                const clW = Math.round(cl / mx * 100);
                const ratio = result.current_ratio;
                const ratioColor = ratio!=null ? band(ratio,2,1) : T.textSoft;
                return (
                  <div style={{marginBottom:16,background:T.card2,borderRadius:10,padding:'14px 16px',border:`1px solid ${T.border}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                      <div style={{fontSize:13,fontWeight:600,color:'#FFFFFF'}}>Liquidity Coverage — Current Assets vs Current Liabilities</div>
                      {ratio!=null&&(
                        <div style={{fontSize:13,fontWeight:700,color:ratioColor}}>
                          Current Ratio: {fmt(ratio,1)}x
                        </div>
                      )}
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:10}}>
                      <div>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#FFFFFF',marginBottom:4}}>
                          <span>Current Assets</span>
                          <span style={{fontWeight:700,color:T.green}}>{fmtK(ca)}</span>
                        </div>
                        <div style={{height:18,background:T.border2,borderRadius:9,overflow:'hidden'}}>
                          <div style={{height:18,borderRadius:9,background:T.green,width:`${caW}%`,transition:'width 0.8s'}}/>
                        </div>
                      </div>
                      <div>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#FFFFFF',marginBottom:4}}>
                          <span>Current Liabilities</span>
                          <span style={{fontWeight:700,color:cl>ca?T.red:T.amber}}>{fmtK(cl)}</span>
                        </div>
                        <div style={{height:18,background:T.border2,borderRadius:9,overflow:'hidden'}}>
                          <div style={{height:18,borderRadius:9,background:cl>ca?T.red:T.amber,width:`${clW}%`,transition:'width 0.8s'}}/>
                        </div>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:T.textSoft,marginTop:10,fontStyle:'italic'}}>
                      {ratio!=null&&ratio>=2 ? '✓ Strong liquidity — assets comfortably cover short-term obligations'
                       : ratio!=null&&ratio>=1 ? '▲ Adequate liquidity — assets cover liabilities with limited buffer'
                       : ratio!=null ? '⚠ Liquidity concern — current liabilities exceed current assets'
                       : 'Current ratio not available'}
                    </div>
                  </div>
                );
              })()}

              <Narr text={narr.solvency} color={T.teal}/>
            </SCard>

            {result.early_warning_count===0?(
              <SCard>
                <div style={{display:'flex',gap:14,alignItems:'center'}}>
                  <div style={{fontSize:36}}>✅</div>
                  <div>
                    <div style={{fontSize:15,fontWeight:700,color:T.green,marginBottom:4}}>Clean Balance Sheet — No Early Warning Signals</div>
                    <div style={{fontSize:13,color:T.textMid}}>
                      All seven solvency stress signals were checked and none were triggered.
                      The balance sheet is conservatively managed with no signs of financial distress.
                      This is the safest solvency profile for a long-term investor.
                    </div>
                  </div>
                </div>
              </SCard>
            ):(
              <SCard title="Early Warning Signals Detected" color={T.red}>
                {result.all_flags?.filter(f=>f?.includes('EW:')).map((f,i)=>(
                  <div key={i} style={{display:'flex',gap:8,padding:'8px 0',borderBottom:`1px solid ${T.border}`,fontSize:13,color:T.redL}}>
                    <span style={{fontWeight:700,flexShrink:0}}>⚠</span>
                    {f.replace('[!] EW: ','')}
                  </div>
                ))}
              </SCard>
            )}

            <SCard title="What This Means for You as an Investor">
              <div style={{fontSize:13,color:T.textMid,lineHeight:1.75}}>
                {/* P1-7 Part 2b: Debt/Equity and Interest Coverage are None
                    ("not applicable"), not 0.0, for Banking/NBFC — require
                    !=null explicitly so these narratives can't fire on a
                    suppressed metric that JS would otherwise coerce to 0. */}
                {result.debt_equity!=null&&result.debt_equity<0.1&&(
                  <p style={{marginBottom:8}}>
                    <span style={{color:T.green,fontWeight:700}}>Negligible Debt: </span>
                    A Debt/Equity ratio of {fmt(result.debt_equity)}x means this company is essentially debt-free.
                    This gives management enormous flexibility — they can invest aggressively in growth without
                    the pressure of debt repayment. It also means no risk of financial distress even in a downturn.
                  </p>
                )}
                {(result.sector==='Banking / NBFC'||result.sector==='Financial Services')&&result.debt_equity==null&&(
                  <p style={{marginBottom:8}}>
                    <span style={{color:T.teal,fontWeight:700}}>Debt/Equity and Interest Coverage — Not Applicable: </span>
                    {(result.all_flags||[]).filter(f=>f?.includes('suppressed for bank')||f?.includes('suppressed for NBFC')).map(f=>f.replace(/^\[i\]\s*/,'')).join(' ')
                      ||'Deposits and borrowings are structural funding for this business, not financial leverage — Debt/Equity and Interest Coverage aren’t meaningful risk metrics here. The Capital Adequacy Ratio (for banks) or RBI norms (for NBFCs) are the relevant capital metrics.'}
                  </p>
                )}
                {result.net_debt<0&&(
                  <p style={{marginBottom:8}}>
                    <span style={{color:T.green,fontWeight:700}}>Net Cash Position: </span>
                    With {fmtCr(Math.abs(result.net_debt))} more cash than debt, the company is a net creditor.
                    This cash can fund acquisitions, pay dividends, buy back shares, or weather a recession —
                    without needing to borrow.
                  </p>
                )}
                {result.interest_coverage!=null&&result.interest_coverage>20&&(
                  <p>
                    <span style={{color:T.green,fontWeight:700}}>Interest Coverage {fmt(result.interest_coverage,0)}x (EBITDA basis): </span>
                    For every rupee of interest payable, the company earns {fmt(result.interest_coverage,0)} rupees
                    of operating profit. This is an exceptionally comfortable cushion — effectively making interest
                    cost irrelevant as a risk factor.
                  </p>
                )}
                {result.interest_coverage==null&&result.debt_equity!=null&&result.debt_equity<=0.1&&(
                  <p>
                    <span style={{color:T.green,fontWeight:700}}>No Interest Expense: </span>
                    The company carries no interest-bearing debt — interest coverage is not applicable.
                  </p>
                )}
              </div>
            </SCard>
          </div>
        )}

        {/* ── TAB 6: GROWTH ──────────────────────────────── */}
        {tab===5&&(
          <div>
            <LayerScoreBanner layer={4} label="Growth Quality" score={result.layer4_score}/>
            <SCard title="Revenue, Profit & Stock — Growth Track Record">
              <BarChart data={[
                {label:'3yr',  value:result.sales_cagr_3yr,  color:'#14B8A6', group:'Sales'},
                {label:'5yr',  value:result.sales_cagr_5yr,  color:'#14B8A6', group:'Sales'},
                {label:'10yr', value:result.sales_cagr_10yr, color:'#5EEAD4', group:'Sales'},
                {label:'3yr',  value:result.pat_cagr_3yr,    color:(result.pat_cagr_3yr??0)>=0?'#34D399':'#F87171', group:'PAT'},
                {label:'5yr',  value:result.pat_cagr_5yr,    color:(result.pat_cagr_5yr??0)>=0?'#34D399':'#F87171', group:'PAT'},
                {label:'3yr',  value:result.stock_cagr_3yr,  color:'#FBBF24', group:'Stock'},
                {label:'5yr',  value:result.stock_cagr_5yr,  color:'#FBBF24', group:'Stock'},
                {label:'10yr', value:result.stock_cagr_10yr, color:'#FCD34D', group:'Stock'},
              ].filter(d=>d.value!=null)}/>
              <div style={{display:'flex',gap:20,marginTop:8,fontSize:12,color:T.textSoft,flexWrap:'wrap'}}>
                <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,background:T.teal,borderRadius:2,display:'inline-block'}}/> Sales CAGR</span>
                <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,background:T.green,borderRadius:2,display:'inline-block'}}/> PAT CAGR</span>
                <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,background:T.amber,borderRadius:2,display:'inline-block'}}/> Stock CAGR</span>
              </div>
              <div style={{fontSize:10,color:T.textSoft,marginTop:6,fontStyle:'italic'}}>* All CAGRs computed on TTM (trailing twelve months) basis. Bars for periods with insufficient data are omitted.</div>
              <div style={{marginTop:12,fontSize:13,color:T.textSoft,lineHeight:1.6}}>
                <strong style={{color:T.text}}>How to read this: </strong>
                When PAT CAGR exceeds Sales CAGR, margins are expanding — the business is becoming more profitable as it grows (operating leverage working in your favour).
                When Stock CAGR significantly exceeds PAT CAGR, the market has expanded the valuation multiple — future growth expectations are being priced in today.
              </div>
              <Narr text={narr.growth} color={T.green}/>
            </SCard>

            <SCard title="Economic Value Added (EVA) — Is Real Wealth Being Created?">
              <div style={{background:T.card2,borderRadius:10,padding:'1rem',marginBottom:14,border:`1px solid ${T.border}`,fontSize:15,color:T.textSoft,lineHeight:1.7}}>
                <strong style={{color:T.text}}>EVA explained for retail investors: </strong>
                A company creates real value only when it earns MORE than its cost of capital (WACC).
                A company can be profitable and still destroy wealth if its returns are below what investors could earn elsewhere.
                EVA = NOPAT − (WACC × Capital Employed). Positive EVA = genuine wealth creation.
              </div>
              <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
                <MCard label="EVA" value={fmtCr(result.eva)} color={result.eva>0?T.green:T.red}
                  highlight={result.eva>0} sub={result.eva>0?'Wealth being created':'Below cost of capital'}/>
                <MCard label="ROCE" value={fmt(result.roce)+'%'} color={T.green} sub="Return earned"/>
                <MCard label="WACC" value={fmt(result.wacc)+'%'} color={T.textMid} sub="Return required"/>
              </div>
              <div style={{background:result.eva>0?`${T.green}15`:`${T.red}15`,borderRadius:10,padding:'12px 16px',border:`1px solid ${result.eva>0?T.green:T.red}40`}}>
                <div style={{fontSize:15,fontWeight:700,color:result.eva>0?T.green:T.red,marginBottom:6}}>
                  {result.eva>0?`✓ ROCE (${fmt(result.roce)}%) exceeds WACC (${fmt(result.wacc)}%) by ${fmt(result.roce-result.wacc)}% — genuine value creation`
                    :`⚠ ROCE (${fmt(result.roce)}%) is below WACC (${fmt(result.wacc)}%) — returns do not cover cost of capital`}
                </div>
                <div style={{fontSize:15,color:T.textMid}}>
                  {result.eva>0
                    ?`For every ₹100 of capital employed, the business generates ₹${fmt(result.roce-result.wacc)} of surplus above the minimum required return. This compounding spread is the foundation of long-term wealth creation.`
                    :`This can be temporary — new investments, acquisitions, or a downturn cycle often cause short-term EVA compression. The question for investors is whether management has a credible path to restoring ROCE above WACC.`}
                </div>
              </div>
            </SCard>

<SCard title="PEG Ratio — Growth-Adjusted Valuation">
              <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:8}}>
                <MCard
                  label="P/E Ratio"
                  value={result.pe_ratio > 0 ? fmt(result.pe_ratio, 1)+'x' : '—'}
                  color={result.pe_ratio > 0
                    ? result.pe_ratio < 20 ? T.green
                    : result.pe_ratio < 35 ? T.amber
                    : T.red : T.textSoft}
                  sub="CMP / EPS"
                />
                <MCard
                  label="PEG Ratio"
                  value={result.peg_ratio > 0 ? result.peg_ratio+'x' : 'N/A'}
                  color={result.peg_ratio > 0
                    ? result.peg_ratio < 1.0 ? T.green
                    : result.peg_ratio < 1.5 ? T.greenL
                    : result.peg_ratio < 2.5 ? T.amber
                    : T.red : T.textSoft}
                  highlight={result.peg_ratio > 0 && result.peg_ratio < 1.5}
                  sub={result.peg_ratio < 1.0 ? 'Undervalued vs growth'
                    : result.peg_ratio < 1.5 ? 'Fairly valued'
                    : result.peg_ratio < 2.5 ? 'Moderately expensive'
                    : result.peg_ratio > 0 ? 'Expensive' : 'Not applicable'}
                />
              </div>
              {result.peg_ratio > 0 && result.peg_note && (
                <div style={{fontSize:13,color:T.textSoft,marginBottom:10,fontStyle:'italic'}}>
                  Growth basis: {result.peg_note.includes('YoY') ? 'Year-on-Year (YoY) PAT growth used' : '3-year PAT CAGR used (YoY excluded as outlier)'}
                </div>
              )}
              <div style={{
                background: T.card2, borderRadius:8, padding:'10px 14px',
                fontSize:15, color:T.textMid, lineHeight:1.6,
                borderLeft:`3px solid ${
                  result.peg_ratio < 1.0 ? T.green
                  : result.peg_ratio < 1.5 ? T.greenL
                  : result.peg_ratio < 2.5 ? T.amber
                  : T.red}`
              }}>
                {result.peg_note || 'PEG ratio not available'}
              </div>
              <div style={{fontSize:14,color:T.textSoft,marginTop:10,lineHeight:1.7}}>
                <strong style={{color:T.textMid}}>How to read PEG: </strong>
                PEG below 1.0 suggests the stock may be undervalued relative to its growth rate.
                PEG of 1.0–1.5 is generally considered fair value. Above 2.5 means you are
                paying a significant premium for growth — justified only if growth is highly
                predictable and sustainable.
              </div>
            </SCard>
            <SCard title="5-Year Revenue & PAT Forecast">
              <div style={{fontSize:13,color:T.textSoft,marginBottom:12}}>
                Based on {result.sales_cagr_3yr!=null?`${fmt(result.sales_cagr_3yr)}% 3-year Sales CAGR`:'historical growth trend'} with health score adjustment. Used as input to DCF — not a guarantee.
              </div>
              {result.forecast_years?.length>0&&(()=>{
                const REV_CLR = '#3B82F6';
                const PAT_CLR = '#39FF14';
                const fData = result.forecast_years.map((yr,i)=>({
                  year: yr,
                  revenue: Math.round(result.revenue_forecast[i]),
                  pat: Math.round(result.pat_forecast[i]),
                }));
                const fmtCrShort = v => v>=1000?'₹'+(v/1000).toFixed(1)+'K Cr':'₹'+v+'Cr';
                const FLabel = color => (props) => {
                  const {x,y,width,value}=props;
                  if(!value) return null;
                  return <text x={x+width/2} y={y-5} textAnchor="middle" fontSize={10} fontWeight={700} fill={color} style={{pointerEvents:'none'}}>{fmtCrShort(value)}</text>;
                };
                return (
                  <>
                    <div style={{display:'flex',gap:16,marginBottom:8}}>
                      <span style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:'#FFFFFF'}}>
                        <span style={{width:12,height:12,background:REV_CLR,borderRadius:2,display:'inline-block'}}/>Revenue
                      </span>
                      <span style={{display:'flex',alignItems:'center',gap:5,fontSize:12,color:'#FFFFFF'}}>
                        <span style={{width:12,height:12,background:PAT_CLR,borderRadius:2,display:'inline-block'}}/>PAT
                      </span>
                    </div>
                    <div style={{height:260}}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RBarChart data={fData} margin={{top:28,right:16,left:8,bottom:4}} barCategoryGap="30%" barGap={4}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5A" vertical={false}/>
                          <XAxis dataKey="year" tick={{fill:'#FFFFFF',fontSize:12,fontWeight:600}} axisLine={false} tickLine={false}/>
                          <YAxis tick={{fill:'#FFFFFF',fontSize:10}} axisLine={false} tickLine={false}
                            tickFormatter={v=>v>=1000?'₹'+(v/1000).toFixed(0)+'K':'₹'+v} width={52}/>
                          <RTooltip
                            formatter={(v,n)=>['₹'+v+'Cr', n==='revenue'?'Revenue':'PAT']}
                            contentStyle={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,fontSize:12}}
                            labelStyle={{color:'#FFFFFF',fontWeight:600}}
                            cursor={{fill:'rgba(255,255,255,0.04)'}}/>
                          <Bar dataKey="revenue" name="revenue" fill={REV_CLR} radius={[4,4,0,0]}>
                            <LabelList dataKey="revenue" content={FLabel('#FFFFFF')}/>
                          </Bar>
                          <Bar dataKey="pat" name="pat" fill={PAT_CLR} radius={[4,4,0,0]}>
                            <LabelList dataKey="pat" content={FLabel(PAT_CLR)}/>
                          </Bar>
                        </RBarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                );
              })()}
            </SCard>
          </div>
        )}

        {/* ── TAB 7: PEERS ───────────────────────────────── */}
        {tab===6&&(
          <div>
            {!peerRequested&&!peerLoading?(
              <SCard title="Peer Comparison">
                <Narr text={narr.peer_intro} color={T.blue}/>
                <div style={{textAlign:'center',padding:'2rem',color:T.textSoft,fontSize:14}}>
                  <div style={{fontSize:32,marginBottom:8}}>🔍</div>
                  <div style={{fontWeight:600,color:T.textMid,marginBottom:4}}>Peer comparison hasn't been run yet</div>
                  <div style={{marginBottom:16}}>Finds size-comparable companies in the same industry and re-runs the full analysis on each — can take up to a couple of minutes.</div>
                  <button onClick={runPeerComparison}
                    style={{background:`linear-gradient(135deg,${T.blue},${T.blueD})`,
                      color:'#fff',border:'none',borderRadius:8,padding:'10px 22px',fontSize:14,
                      fontWeight:600,cursor:'pointer'}}>
                    Run Peer Comparison
                  </button>
                </div>
              </SCard>
            ):peerLoading&&peerRes.length===0?(
              <SCard title="Peer Comparison">
                <Narr text={narr.peer_intro} color={T.blue}/>
                <div style={{textAlign:'center',padding:'2rem',color:T.textSoft,fontSize:14}}>
                  <div style={{fontSize:36,marginBottom:12}}>⏳</div>
                  <div style={{fontWeight:600,color:T.textMid,marginBottom:6}}>Finding comparable peers...</div>
                  <div>Selecting most size-comparable companies from the same industry</div>
                </div>
              </SCard>
            ):peerRes.length===0?(
              <SCard title="Peer Comparison">
                <Narr text={narr.peer_intro} color={T.blue}/>
                <div style={{padding:'1.5rem 0'}}>
                  {peerError?(
                    <div style={{textAlign:'center',marginBottom:'1.25rem',color:'#FF6B00',fontSize:13,fontWeight:600}}>
                      {peerError}
                    </div>
                  ):(
                    <div style={{textAlign:'center',marginBottom:'1.25rem',color:T.textSoft,fontSize:14}}>
                      <div style={{fontSize:32,marginBottom:8}}>📊</div>
                      <div style={{fontWeight:600,color:T.textMid,marginBottom:4}}>No peers found automatically</div>
                      <div>Search for a company to compare manually:</div>
                    </div>
                  )}
                  <div style={{display:'flex',gap:8,maxWidth:440,margin:'0 auto'}}>
                    <CompanySearch
                      placeholder="Search peer company..."
                      onSelect={(t,n)=>setOverrideTicker(t)}
                      style={{flex:1}}
                      inputStyle={{fontSize:13,padding:'8px 12px'}}
                    />
                    <button onClick={()=>runPeerOverride(overrideTicker)} disabled={!overrideTicker||peerLoading}
                      style={{background:overrideTicker?`linear-gradient(135deg,${T.blue},${T.blueD})`:'#1A2A3D',
                        color:overrideTicker?'#fff':T.textSoft,border:'none',borderRadius:8,
                        padding:'8px 18px',fontSize:13,fontWeight:600,whiteSpace:'nowrap',
                        cursor:overrideTicker?'pointer':'not-allowed'}}>
                      Compare
                    </button>
                  </div>
                </div>
              </SCard>
            ):(()=>{
              const all=[result,...peerRes];
              const metrics=[
                {label:'Health Score',    key:'health_score',       unit:'/100', g:70,o:50,  higher:true},
                {label:'ROCE',            key:'roce',               unit:'%',    g:18,o:12,  higher:true},
                {label:'ROE',             key:'roe',                unit:'%',    g:15,o:10,  higher:true},
                {label:'EBIT Margin',     key:'ebit_margin',        unit:'%',    g:14,o:8,   higher:true},
                {label:'CFO/PAT',         key:'cfo_pat',            unit:'x',    g:1,o:0.7,  higher:true},
                {label:'DICC',            key:'dicc',               unit:'d',    g:30,o:60,  higher:false},
                {label:'D/E Ratio',       key:'debt_equity',        unit:'x',    g:0.3,o:1,  higher:false},
                {label:'P/E Ratio',       key:'pe_ratio',           unit:'x',    g:15,o:25,  higher:false},
                {label:'Sales CAGR 3yr',  key:'sales_cagr_3yr',     unit:'%',    g:15,o:8,   higher:true},
                {label:'PAT CAGR 3yr',    key:'pat_cagr_3yr',       unit:'%',    g:15,o:5,   higher:true},
                {label:'Blended IV',      key:'blended_iv',         unit:'₹',    g:0,o:0,    higher:true},
                {label:'Margin of Safety (MoS) %', key:'margin_of_safety', unit:'%', g:20,o:0, higher:true, tooltip:'Margin of Safety = how far the current price is below intrinsic value. We look for 20% or more before recommending BUY.'},
              ];
              const maxVals=metrics.map(m=>Math.max(...all.map(r=>Math.abs(r[m.key]||0))));
              return (
                <>
                <SCard title="Side-by-Side Comparison">
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead>
                        <tr style={{borderBottom:`2px solid ${T.border2}`}}>
                          <th style={{textAlign:'left',padding:'8px 10px',color:T.textSoft,fontWeight:600,minWidth:120}}>Metric</th>
                          {all.map((r,i)=>(
                            <th key={i} style={{textAlign:'center',padding:'8px 10px',color:i===0?T.amber:T.textMid,fontWeight:600,minWidth:110}}>
                              <div>{i===0?'★ ':''}{r.company_name?.split(' ').slice(0,2).join(' ')}</div>
                              <div style={{fontSize:11,fontWeight:500,color:T.textSoft,marginTop:3}}>{r.cmp>0?fmtRs(r.cmp):'—'}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{borderBottom:`1px solid ${T.border}`,background:T.card2}}>
                          <td style={{padding:'8px 10px',fontWeight:500,color:T.textMid}}>NSE Ticker</td>
                          {all.map((r,i)=>(
                            <td key={i} style={{padding:'8px 10px',textAlign:'center',fontWeight:600,color:i===0?T.amber:T.textMid,fontSize:12}}>
                              {r.ticker||'—'}
                            </td>
                          ))}
                        </tr>
                        {metrics.map((m,mi)=>{
                          // P1-7 Part 2b: rawVal keeps null ("not applicable",
                          // e.g. D/E for a bank) distinct from a real 0 — only
                          // the bar width and the best-of-column comparison
                          // use the ||0-coerced val, display and colour use
                          // rawVal so a suppressed metric shows "—" with a
                          // neutral colour, never a fake "0.0".
                          const vals=all.map(r=>r[m.key]||0);
                          const best=m.higher?Math.max(...vals):Math.min(...vals.filter(v=>v>0));
                          return (
                            <tr key={m.label} style={{borderBottom:`1px solid ${T.border}`}}>
                              <td style={{padding:'8px 10px',fontWeight:500,color:T.textMid}}>{m.tooltip?<TipSpan tip={m.tooltip}>{m.label}</TipSpan>:m.label}</td>
                              {all.map((r,i)=>{
                                const rawVal=r[m.key];
                                const val=rawVal==null?0:rawVal;
                                const isBest=rawVal!=null&&Math.abs(val-best)<0.01;
                                const clr=m.higher?band(rawVal,m.g,m.o):bandLow(rawVal,m.g,m.o);
                                return (
                                  <td key={i} style={{padding:'8px 10px',textAlign:'center'}}>
                                    <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:4}}>
                                      <CompBar value={Math.abs(val)} max={maxVals[mi]||1} color={clr}/>
                                      <span style={{fontWeight:isBest?700:500,color:clr,minWidth:55,textAlign:'right',fontSize:12}}>
                                        {rawVal==null?'—':(m.key==='blended_iv'?fmtRs(rawVal):fmt(rawVal))}{(rawVal==null||m.key==='blended_iv')?'':m.unit}
                                        {isBest&&<span style={{marginLeft:2,fontSize:9}}>★</span>}
                                      </span>
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        <tr style={{background:T.card2}}>
                          <td style={{padding:'8px 10px',fontWeight:600,color:T.text}}>Verdict</td>
                          {all.map((r,i)=>{
                            const vc2=vCfg[r.verdict]||vCfg.WATCH;
                            return (
                              <td key={i} style={{padding:'8px 10px',textAlign:'center'}}>
                                <span style={{background:vc2.bg,color:vc2.cardColor,padding:'3px 10px',borderRadius:12,fontSize:11,fontWeight:700}}>
                                  {r.verdict}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </SCard>
                <SCard title="Compare with Another Company">
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <CompanySearch
                      placeholder="Search company to compare..."
                      onSelect={(t,n)=>setOverrideTicker(t)}
                      style={{flex:1}}
                      inputStyle={{fontSize:13,padding:'8px 12px'}}
                    />
                    <button onClick={()=>runPeerOverride(overrideTicker)} disabled={!overrideTicker||peerLoading}
                      style={{background:overrideTicker&&!peerLoading?`linear-gradient(135deg,${T.blue},${T.blueD})`:'#1A2A3D',
                        color:overrideTicker&&!peerLoading?'#fff':T.textSoft,border:'none',borderRadius:8,
                        padding:'8px 18px',fontSize:13,fontWeight:600,whiteSpace:'nowrap',
                        cursor:overrideTicker&&!peerLoading?'pointer':'not-allowed'}}>
                      {peerLoading?'Loading...':'Compare'}
                    </button>
                  </div>
                </SCard>
                </>
              );
            })()}
          </div>
        )}

        {/* ── TAB 8: VALUATION ───────────────────────────── */}
        {tab===7&&(
          <div>
            <div style={{background:T.card,borderLeft:'4px solid #00C2A8',padding:20,marginBottom:24}}>
              <div style={{fontSize:13,fontWeight:600,letterSpacing:'2px',color:'#00C2A8',marginBottom:14}}>HOW WE VALUE THIS STOCK</div>
              <div style={{fontSize:14,color:'#FFFFFF',lineHeight:1.8}}>
                <p style={{margin:'0 0 10px 0'}}>We use a three-method blended valuation to arrive at Intrinsic Value (IV) — the price a stock is worth based on fundamentals, not market sentiment.</p>
                <p style={{margin:'0 0 10px 0'}}>① DCF (Discounted Cash Flow) — Projects future free cash flows and discounts them to today's value using WACC (Weighted Average Cost of Capital). Weight varies by sector.</p>
                <p style={{margin:'0 0 10px 0'}}>② Multiples — Values the stock using Industry P/E and EV/EBITDA benchmarks relative to sector peers.</p>
                <p style={{margin:'0 0 10px 0'}}>③ DDM (Dividend Discount Model) — Applied only when dividend yield exceeds 1%. Weight increases with dividend consistency.</p>
                <p style={{margin:'0 0 10px 0'}}>Bear / Central / Bull scenarios are derived as follows: Central = blended IV from all three methods. Bear = Central minus 20% (conservative assumptions). Bull = Central plus 20% (optimistic assumptions).</p>
                <p style={{margin:0}}>Margin of Safety (MoS) = how far the current market price is below intrinsic value. A MoS of 20% or more is our threshold for a BUY signal.</p>
              </div>
            </div>
            <SCard title="Three-Method Blended Valuation">
              {/* ── Bear / Central / Bull range ── */}
              <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:10}}>
                {[
                  {label:'Bear IV',    val:result.blended_iv_low,  bg:'#7F1D1D', border:'#FF2D2D', color:'#FFFFFF'},
                  {label:'Central IV', val:result.blended_iv,      bg:'#1A3A2A', border:'#39FF14', color:'#39FF14'},
                  {label:'Bull IV',    val:result.blended_iv_high, bg:'#1A2A1A', border:'#00E676', color:'#00E676'},                ].map(({label,val,bg,border,color})=>(
                  <div key={label} style={{background:bg,border:`1px solid ${border}40`,borderRadius:10,padding:'14px',textAlign:'center'}}>
                    <div style={{fontSize:11,color:'#FFFFFF',marginBottom:6,fontWeight:500}}>{label}</div>
                    <div style={{fontSize:28,fontWeight:700,color}}>{fmtRs(val)}</div>
                  </div>
                ))}
              </div>

              {/* ── Valuation Multiples & Verdict ── */}
              {(()=>{
                const pe   = result.pe_ratio  || 0;
                const indPe= result.industry_pe|| 0;
                const peg  = result.peg_ratio  || 0;
                const evEb = result.ev_ebitda  || 0;
                const peCol= indPe>0 ? (pe>indPe*1.5?'#FF2D2D':pe>indPe?'#FFD700':'#39FF14') : '#00FFFF';
                const evCol= indPe>0 ? (evEb>indPe*1.5?'#FF2D2D':evEb>indPe?'#FFD700':'#39FF14') : '#00FFFF';
                const pegCol= peg>2?'#FF2D2D':peg>=1?'#FFD700':'#39FF14';
                const MCard=({val,valCol,title,sub,tip})=>(
                  <div style={{background:T.card,borderRadius:12,padding:16}}>
                    <div style={{fontSize:12,color:'#FFFFFF',marginBottom:6,fontWeight:500}}>
                      {tip?<TipSpan tip={tip}>{title}</TipSpan>:title}
                    </div>
                    <div style={{fontSize:'1.8rem',fontWeight:800,color:valCol,lineHeight:1.1}}>{val}</div>
                    {sub&&<div style={{fontSize:12,color:'#FFFFFF',marginTop:6,opacity:0.7}}>{sub}</div>}
                  </div>
                );
                return (
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:13,letterSpacing:2,color:'#00C2A8',fontWeight:600,marginBottom:12}}>VALUATION MULTIPLES &amp; VERDICT</div>
                    {/* Row 1 — four metric cards */}
                    <div className="mob-2col" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:16}}>
                      <MCard val={pe>0?`${fmt(pe,1)}x`:'N/A'} valCol={peCol}
                        title="Stock P/E"
                        tip="How much investors pay per ₹1 of profit. A P/E of 30 means the stock costs 30× its annual earnings. Higher P/E = market expects strong growth ahead. Compare with Industry P/E to judge if the stock is cheap or expensive vs its peers."
                        sub="Price to Earnings"/>
                      <MCard val={indPe>0?`${fmt(indPe,1)}x`:'N/A'} valCol='#00FFFF'
                        title="Industry P/E"
                        tip="The average P/E of all listed companies in the same sector. Use this as a yardstick — if Stock P/E is above Industry P/E the stock trades at a premium to peers; below means a relative discount."
                        sub="Sector benchmark"/>
                      <MCard val={peg>0?`${fmt(peg,2)}x`:'N/A'} valCol={pegCol}
                        title="PEG Ratio"
                        tip="PEG = P/E ÷ Earnings Growth Rate. It checks whether you are paying a fair price for the growth on offer. PEG below 1 = potentially undervalued. Around 1 = fairly priced. Above 2 = paying a steep premium for growth — approach with caution."
                        sub={result.peg_note||'Price / Earnings / Growth'}/>
                      <MCard val={evEb>0?`${fmt(evEb,1)}x`:'N/A'} valCol={evCol}
                        title="EV / EBITDA"
                        tip="A more complete version of P/E — it values the whole business (equity + debt) against its operating earnings before interest, tax, depreciation and amortisation. Lower is cheaper. Rough benchmarks: below 15x cheap, 15–25x fair, above 25x expensive. Useful for comparing companies with different debt levels."
                        sub="Enterprise value multiple"/>
                    </div>
                    {/* Row 2 — verdict reason full width */}
                    <div style={{background:T.card,borderRadius:12,padding:16}}>
                      <div style={{fontSize:12,color:'#00C2A8',fontWeight:600,marginBottom:8,letterSpacing:1}}>WHY THIS VERDICT</div>
                      <div style={{fontSize:15,color:'#FFFFFF',fontWeight:500,marginBottom:12}}>
                        {result.verdict_reason||'—'}
                      </div>
                      {(result.weight_notes||[]).length>0&&(
                        <>
                          <div style={{fontSize:12,color:'#00C2A8',fontWeight:600,marginBottom:6,letterSpacing:1}}>BLEND WEIGHTS USED:</div>
                          {result.weight_notes.map((n,i)=>(
                            <div key={i} style={{fontSize:13,color:'#FFD700',marginBottom:3}}>{n}</div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ── Blended IV + MoS row ── */}
              <div style={{background:`${T.amber}18`,border:`2px solid ${T.amber}`,borderRadius:12,marginBottom:10}}>
                <div className="cal-iv-row" style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',padding:24}}>
                  {/* Left — IV */}
                  <div>
                    <div style={{fontSize:13,color:'#FFFFFF'}}>Blended Intrinsic Value per Share</div>
                    <div style={{fontSize:'2.8rem',fontWeight:800,color:'#FFD700'}}>{fmtRs(result.blended_iv)}</div>
                  </div>
                  {/* Centre — CMP */}
                  <div style={{textAlign:'center'}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#FFFFFF'}}>Current Market Price</div>
                    <div style={{fontSize:'2.8rem',fontWeight:800,color:'#00FFFF'}}>{fmtRs(result.cmp)}</div>
                  </div>
                  {/* Right — MoS and comment */}
                  <div className="cal-iv-mos" style={{textAlign:'right',maxWidth:'300px',wordWrap:'break-word',whiteSpace:'normal'}}>
                    <div style={{fontSize:'1.2rem',fontWeight:700,color:result.margin_of_safety>=0?T.green:T.red}}>
                      <TipSpan tip="Margin of Safety = how far the current price is below intrinsic value. We look for 20% or more before recommending BUY.">Margin of Safety (MoS)</TipSpan>: {result.margin_of_safety>=0?'+':''}{fmt(result.margin_of_safety)}%
                    </div>
                    {/* R1-b: the backend's own verdict_reason (compute_verdict/
                        finalize_verdict) — not a locally re-derived margin-of-
                        safety threshold, which could disagree with the real
                        verdict (PEG caps, bear-case cap, health gates, etc.
                        all live only in the backend). */}
                    <div style={{fontSize:'1.1rem',fontWeight:700,color:'#FFFFFF',marginTop:3,whiteSpace:'normal',wordBreak:'break-word',maxWidth:'400px',marginLeft:'auto'}}>
                      {result.verdict_reason||''}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Market mood pill ── */}
              {result.market_mood&&(
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                  <span style={{fontSize:16,fontWeight:600,color:'#FFFFFF'}}>Market mood:</span>
                  <span style={{
                    fontSize:15,fontWeight:700,padding:'6px 16px',borderRadius:12,
                    background:result.market_mood?.toLowerCase()==='euphoric'?'#3D1515'
                      :result.market_mood?.toLowerCase()==='elevated'?'#3D2800'
                      :result.market_mood?.toLowerCase()==='depressed'?'#0A2E1F'
                      :'#0F2A4A',
                    color:result.market_mood?.toLowerCase()==='euphoric'?'#FCA5A5'
                      :result.market_mood?.toLowerCase()==='elevated'?'#FCD34D'
                      :result.market_mood?.toLowerCase()==='depressed'?'#6EE7B7'
                      :'#E0E0E0',
                  }}>{result.market_mood?.charAt(0).toUpperCase()+(result.market_mood?.slice(1)||'')}</span>
                </div>
              )}

              {/* ── IV range note ── */}
              {result.iv_range_note&&(
                <div style={{fontSize:12,color:T.textSoft,padding:'10px 14px',background:T.card2,
                  borderRadius:8,borderLeft:`3px solid ${T.amber}`,lineHeight:1.6,fontStyle:'italic'}}>
                  {result.iv_range_note}
                </div>
              )}

              {/* Valuation method comparison chart */}
              <div style={{marginTop:12}}>
                <div style={{fontSize:12,color:T.textSoft,marginBottom:4}}>Valuation Method Comparison — vs Current Market Price</div>
                <ValuationChart
                  dcf={result.dcf_value} multiples={result.multiples_value}
                  ddm={result.ddm_value} blended={result.blended_iv} cmp={result.cmp}/>
              </div>
              <Narr text={narr.valuation} color={T.amber}/>
            </SCard>

            {/* Interactive Sliders — R1-b: every number below comes from
                POST /revalue (the same engine /analyse uses), never
                computed here. */}
            {result.verdict === 'NOT_RATED' ? (
              <SCard title="Stress Test — Adjust Assumptions & See Impact" color={T.purple}>
                <div style={{fontSize:13,color:T.textSoft,lineHeight:1.6}}>
                  Not available — this analysis has too little data for a reliable valuation
                  (verdict NOT_RATED).
                </div>
              </SCard>
            ) : (
            <SCard title="Stress Test — Adjust Assumptions & See Impact" color={T.purple}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'1.25rem'}}>
                <div style={{fontSize:13,color:T.textSoft,lineHeight:1.6,maxWidth:'80%'}}>
                  Move the sliders to see how changes in key assumptions affect the intrinsic value.
                  This helps you understand the range of outcomes and which variables matter most.
                </div>
                {(wTouched||gTouched||tgTouched)&&(
                  <button onClick={resetSliders} style={{fontSize:12,fontWeight:600,color:T.purple,
                    background:'transparent',border:`1px solid ${T.purple}`,borderRadius:6,
                    padding:'6px 12px',cursor:'pointer',whiteSpace:'nowrap'}}>Reset</button>
                )}
              </div>
              {result.dcf_ranges&&[
                {key:'wacc', label:`WACC (Cost of Capital): ${fmt(sWacc)}%`, val:sWacc,
                 onChange:v=>{setSWacc(v); setWTouched(true);},
                 min:result.dcf_ranges.wacc.min, max:result.dcf_ranges.wacc.max, step:0.1,
                 note:'Higher WACC = lower IV. Reflects risk — riskier businesses deserve a higher discount rate.'},
                {key:'terminal_growth', label:`Terminal Growth Rate: ${fmt(sTg)}%`, val:sTg,
                 onChange:v=>{setSTg(v); setTgTouched(true);},
                 min:result.dcf_ranges.terminal_growth.min, max:result.dcf_ranges.terminal_growth.max, step:0.1,
                 note:'India long-run nominal GDP growth assumed at 5%. Adjust based on sector outlook.'},
                {key:'growth_yr1', label:`Starting growth rate (fades toward terminal growth): ${fmt(sGrow)}%`, val:sGrow,
                 onChange:v=>{setSGrow(v); setGTouched(true);},
                 min:result.dcf_ranges.growth_yr1.min, max:result.dcf_ranges.growth_yr1.max, step:0.5,
                 note:'This is the starting point only — it fades smoothly toward the terminal growth rate above over the 5-year forecast, not a flat rate for years 1-3.'},
              // Keyed by a STABLE identifier, not the label text — the label
              // embeds the live value, so keying on it would remount the
              // <input> on every single value change (losing drag focus
              // mid-gesture in a real browser, and silently detaching any
              // already-bound event listener in a test).
              ].map(({key,label,val,onChange,min,max,step,note})=>(
                <div key={key} style={{marginBottom:'1.25rem'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                    <span style={{fontSize:13,fontWeight:500,color:T.textMid}}>{label}</span>
                    <span style={{fontSize:12,color:T.textSoft}}>{min}% — {max}%</span>
                  </div>
                  <input type="range" min={min} max={max} step={step} value={val!=null?val:min}
                    onChange={e=>onChange(Number(e.target.value))}
                    style={{width:'100%',accentColor:T.purple,marginBottom:4}}/>
                  <div style={{fontSize:11,color:T.textSoft}}>{note}</div>
                </div>
              ))}

              {revalError&&(
                <div style={{padding:'10px 14px',marginBottom:12,background:`${T.red}18`,
                  border:`1px solid ${T.red}`,borderRadius:8,fontSize:13,color:T.textMid}}>
                  {revalError.message}
                  {revalError.reason==='no_recent_analysis'&&(
                    <button onClick={()=>analyse(liveMode)} style={{marginLeft:12,fontSize:12,fontWeight:600,
                      color:T.red,background:'transparent',border:`1px solid ${T.red}`,borderRadius:6,
                      padding:'4px 10px',cursor:'pointer'}}>Run analysis again</button>
                  )}
                </div>
              )}

              {displayedValuation&&displayedValuation.blended!=null&&(
                <div style={{background:`${T.purple}20`,border:`1.5px solid ${T.purple}`,borderRadius:10,
                  padding:24,opacity:revalLoading?0.5:1,transition:'opacity 0.15s',position:'relative'}}>
                  {revalLoading&&(
                    <div style={{position:'absolute',top:12,right:12,width:16,height:16,
                      border:`2px solid ${T.purple}`,borderTopColor:'transparent',borderRadius:'50%',
                      animation:'spin 0.8s linear infinite'}}/>
                  )}
                  <div className="mob-1col" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:'#FFFFFF',marginBottom:4}}>Blended Intrinsic Value at Selected Assumptions</div>
                      <div style={{fontSize:'2.8rem',fontWeight:800,color:'#39FF14',lineHeight:1}}>{fmtRs(Math.round(displayedValuation.blended))}</div>
                      <div style={{marginTop:12}}>
                        <div style={{fontSize:12,fontWeight:500,color:'#FFFFFF',marginBottom:2}}>DCF Value at Selected Assumptions</div>
                        <div style={{fontSize:'1.6rem',fontWeight:700,color:'#00FFFF'}}>
                          {displayedValuation.dcfValue!=null?fmtRs(Math.round(displayedValuation.dcfValue)):'Not meaningful'}
                        </div>
                        {/* P3-8a: show WHY instead of a bare "N/A"/raw null —
                            never fabricate a number here either. */}
                        {displayedValuation.dcfValue==null&&displayedValuation.dcfNotMeaningfulNote&&(
                          <div style={{fontSize:11,color:T.textSoft,marginTop:4,fontStyle:'italic',maxWidth:280,lineHeight:1.4}}>
                            {displayedValuation.dcfNotMeaningfulNote}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="cal-stress-right" style={{textAlign:'right'}}>
                      <div style={{fontSize:13,fontWeight:600,color:'#FFFFFF',marginBottom:4}}>Margin of Safety at Selected Assumptions</div>
                      <div style={{fontSize:'2.2rem',fontWeight:800,color:'#FFD700',lineHeight:1}}>
                        {displayedValuation.mos!=null?`${displayedValuation.mos>=0?'+':''}${displayedValuation.mos}%`:'N/A'}
                      </div>
                    </div>
                  </div>
                  <div style={{marginTop:16,paddingTop:14,borderTop:`1px solid ${T.border}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',flexWrap:'wrap',gap:8}}>
                      <span style={{fontSize:13,fontWeight:600,color:'#FFFFFF'}}>
                        Verdict at these assumptions: <span style={{color:T.amberL}}>{displayedValuation.verdict||'—'}</span>
                      </span>
                      {displayedValuation.verdictChanged&&(
                        <span style={{fontSize:11,fontWeight:700,color:T.amber,border:`1px solid ${T.amber}`,
                          borderRadius:12,padding:'2px 10px'}}>changed from the analysis</span>
                      )}
                    </div>
                    {displayedValuation.verdictReason&&(
                      <div style={{fontSize:12,color:T.textSoft,marginTop:6,lineHeight:1.5}}>{displayedValuation.verdictReason}</div>
                    )}
                    {displayedValuation.assumptionsUsed&&displayedValuation.assumptionsUsed.clamped&&displayedValuation.assumptionsUsed.clamped.length>0&&(
                      <div style={{fontSize:11,color:T.amberL,marginTop:8,fontStyle:'italic'}}>
                        {displayedValuation.assumptionsUsed.clamped.join('; ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </SCard>
            )}

            {/* Sensitivity Table — R1-b: reads displayedValuation.sensitivity,
                which is /revalue's own grid once a slider has been touched,
                falling back to /analyse's original grid otherwise. */}
            {displayedValuation&&displayedValuation.sensitivity&&Object.keys(displayedValuation.sensitivity).length>0&&(
              <SCard title="DCF Sensitivity Matrix — IV per Share (₹)">
                <div style={{fontSize:12,color:T.textSoft,marginBottom:10}}>Rows = WACC · Columns = Terminal Growth Rate · Green = <TipSpan tip="Margin of Safety = how far the current price is below intrinsic value. We look for 20% or more before recommending BUY.">Margin of Safety (MoS)</TipSpan> ≥20% · Amber = <TipSpan tip="Margin of Safety = how far the current price is below intrinsic value. We look for 20% or more before recommending BUY.">Margin of Safety (MoS)</TipSpan> 0-20%</div>
                <div style={{overflowX:'auto'}}>
                  <table style={{borderCollapse:'collapse',fontSize:12,width:'100%'}}>
                    <thead>
                      <tr style={{background:T.card2}}>
                        <th style={{padding:'8px 12px',textAlign:'center',color:T.textSoft,border:`1px solid ${T.border}`,fontSize:15,fontWeight:600}}>WACC \ g</th>
                        {Object.keys(displayedValuation.sensitivity[Object.keys(displayedValuation.sensitivity)[0]]||{}).sort((a,b)=>Number(a)-Number(b)).map(g=>(
                          <th key={g} style={{padding:'8px 12px',textAlign:'center',color:T.textSoft,border:`1px solid ${T.border}`,fontSize:15,fontWeight:600}}>g={Number(g)}%</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(displayedValuation.sensitivity).sort((a,b)=>Number(a)-Number(b)).map(wKey=>(
                        <tr key={wKey}>
                          <td style={{padding:'8px 12px',textAlign:'center',fontWeight:600,background:T.card2,border:`1px solid ${T.border}`,color:T.textMid,fontSize:15}}>{Number(wKey)}%</td>
                          {Object.keys(displayedValuation.sensitivity[wKey]||{}).sort((a,b)=>Number(a)-Number(b)).map(g=>{
                            const iv=displayedValuation.sensitivity[wKey][g];
                            const mos2=iv>0?((iv-(result.cmp||0))/iv)*100:0;
                            const bg=mos2>=20?`${T.green}25`:mos2>=0?`${T.amber}20`:T.card;
                            const clr=mos2>=20?'#39FF14':mos2>=0?'#FFD700':mos2>=-20?'#FF8C00':'#FF2D2D';
                            return (
                              <td key={g} style={{padding:'8px 12px',textAlign:'center',background:bg,border:`1px solid ${T.border}`,fontWeight:700,color:clr,fontSize:16}}>
                                {fmtRs(iv)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SCard>
            )}
          </div>
        )}

        {/* ── TAB 9: GLOSSARY ─────────────────────────────── */}
        {tab===8&&(
          <GlossaryTab/>
        )}

        {/* Disclaimer */}
        <div style={{fontSize:11,color:T.textSoft,padding:'1rem 0',lineHeight:1.7,borderTop:`1px solid ${T.border}`,marginTop:8}}>
          <strong style={{color:T.textMid}}>Disclaimer:</strong> Data from Screener.in and Yahoo Finance. For educational and research purposes only —
          not SEBI-registered investment advice. Always read the latest annual report before making investment decisions.
          Past performance does not guarantee future results. © Akrobourn Consultants LLP
        </div>
      </div>
      {protectionToast && (
        <div role="status" aria-live="polite" style={{
          position:'fixed',bottom:32,left:'50%',transform:'translateX(-50%)',
          background:'rgba(15,27,45,0.96)',color:'#F0F4F8',padding:'10px 20px',
          borderRadius:8,border:`1px solid ${T.border2}`,
          boxShadow:'0 8px 24px rgba(0,0,0,0.6)',fontSize:13,zIndex:9999,
          pointerEvents:'none',userSelect:'none',
        }}>{protectionToast}</div>
      )}
    </div>
  );
}

function GlossaryTab() {
  const [search,setSearch] = useState('');
  const entries = Object.entries(GLOSSARY);
  const filtered = search.length<1 ? entries : entries.filter(([key,val])=>
    key.toLowerCase().includes(search.toLowerCase())||
    val.full.toLowerCase().includes(search.toLowerCase())||
    val.plain.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div style={{padding:'0 0 24px 0'}}>
      <div style={{fontSize:20,fontWeight:700,color:T.text,marginBottom:6}}>Glossary of Terms</div>
      <div style={{fontSize:13,color:T.textSoft,marginBottom:16,lineHeight:1.6}}>
        Plain English explanations of every financial term used in Callidus.
        Hover over any metric label with a <span style={{color:T.amber,fontWeight:700}}>?</span> in the analysis tabs to see the explanation inline.
      </div>
      <input
        placeholder="Search terms, abbreviations or descriptions..."
        value={search}
        onChange={e=>setSearch(e.target.value)}
        style={{width:'100%',padding:'10px 14px',marginBottom:20,boxSizing:'border-box',
          background:T.card2,border:`1px solid ${T.border2}`,
          borderRadius:8,fontSize:13,color:T.text,outline:'none'}}
      />
      {filtered.length===0&&(
        <div style={{fontSize:13,color:T.textSoft,padding:'16px 0'}}>No terms match "{search}"</div>
      )}
      {filtered.map(([key,val])=>(
        <div key={key} style={{padding:'12px 16px',marginBottom:8,
          background:T.card,borderRadius:8,border:`1px solid ${T.border}`}}>
          <div style={{display:'flex',alignItems:'baseline',gap:10,marginBottom:4}}>
            <span style={{fontWeight:700,color:T.amber,fontSize:15,minWidth:120}}>{key}</span>
            <span style={{color:T.textMid,fontSize:12}}>{val.full}</span>
          </div>
          <div style={{fontSize:13,color:T.textSoft,lineHeight:1.65}}>{val.plain}</div>
        </div>
      ))}
    </div>
  );
}

function Header() {
  const handleSignOut = async () => {
    await supabase.auth.signOut()
  }

  return (
    <div className="cal-header" style={{background:`linear-gradient(135deg,#0F1B2D,#1A2A3D)`,
      padding:'0.75rem 2rem',display:'flex',alignItems:'center',gap:'1rem',
      boxShadow:'0 2px 12px rgba(0,0,0,0.5)',borderBottom:'1px solid #2A3F5A'}}>
      <span style={{fontFamily:"'Playfair Display',Georgia,serif",color:'#FFFFFF',fontSize:28,fontWeight:700,letterSpacing:'1px'}}>Callidus</span>
      <span className="cal-header-sub" style={{color:'#E0E0E0',fontSize:12}}>Investment Analysis · Long-Term Retail Investors</span>
      <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:'1.25rem'}}>
        <span style={{fontSize:11,color:'#D0D0D0'}}>© Akrobourn Consultants LLP</span>
        <button
          onClick={handleSignOut}
          style={{fontSize:12,fontWeight:600,color:'#F59E0B',background:'transparent',
            border:'1px solid #F59E0B',borderRadius:6,padding:'4px 12px',
            cursor:'pointer',letterSpacing:'0.03em',transition:'all 0.2s'}}
          onMouseEnter={e=>{e.currentTarget.style.background='#F59E0B';e.currentTarget.style.color='#0A0B14';}}
          onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='#F59E0B';}}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}